import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Building2, Users, Bot, MessageSquare, Mail, Star, Crown, Trash2, Shield, Search,
  Lock, Plus, X, Package, DollarSign, BarChart3, Download, ShieldCheck, Power, Sparkles,
  CreditCard, ExternalLink, UserCheck, CheckCircle, ChevronDown, ChevronRight, Pencil,
  RotateCcw, History, AlertTriangle,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "wouter";
import React, { useEffect, useMemo, useState } from "react";
import { apiRequest, queryClient, getAuthToken } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import { PageShell, PageStat } from "@/components/page-shell";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ComplianceEnableOverrideDialog } from "@/components/compliance-enable-override-dialog";

// ────────────────────────────────────────────────────────────────────────────
// Tenant avatar helpers
// ────────────────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
  "bg-blue-500",   "bg-violet-500", "bg-emerald-500", "bg-amber-500",
  "bg-rose-500",   "bg-cyan-500",   "bg-indigo-500",  "bg-teal-500",
  "bg-orange-500", "bg-pink-500",   "bg-sky-500",     "bg-lime-600",
];
function tenantInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
function tenantAvatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

// ────────────────────────────────────────────────────────────────────────────
// Types — kept loose since these endpoints aren't typed centrally yet.
// ────────────────────────────────────────────────────────────────────────────
type Plan = {
  id: string; name: string; slug: string; price: number;
  botLimit: number; agentLimit: number | null;
  monthlyMessages: number; features: string[] | null;
  isActive: string | null;
  billingInterval?: "month" | "year" | null;
  stripeProductId?: string | null;
  stripePriceId?: string | null;
};
type OrgRow = {
  id: string; name: string; slug: string; plan: string;
  botLimit: number; agentLimit: number; monthlyMessageLimit: number;
  messagesUsed: number; brandColor?: string | null; logo?: string | null;
  tagline?: string | null; stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null; subscriptionStatus?: string | null;
  createdAt: string; isSuspended?: boolean; smtpEnabled?: boolean;
  aiProviderConfigured?: boolean;
};
type MasterAdmin = {
  id: string; email: string; name: string; role: string;
  organizationId: string; active: string | null; createdAt?: string;
};
type AnalyticsTenant = {
  id: string; name: string; slug: string; plan: string | null;
  planPrice: number; messagesUsed: number; monthlyMessageLimit: number;
  botCount: number; userCount: number; conversationCount: number;
  createdAt?: string;
};
type Analytics = {
  tenants: AnalyticsTenant[];
  summary: {
    activeTenantCount: number;
    monthlyRecurringRevenueCents: number;
    totalConversations: number;
    totalMessagesUsed: number;
  };
};
type BillingTenant = {
  id: string; name: string; slug: string;
  plan: string | null; planName: string; planPrice: number;
  subscriptionStatus: string; hasStripe: boolean;
  subscriptionStartedAt: string | null;
  subscriptionExpiresAt: string | null;
  messagesUsed: number; monthlyMessageLimit: number;
  botCount: number; userCount: number;
  orgAdminEmail: string | null; orgAdminName: string | null;
  createdAt: string; isSuspended: boolean;
};

type PendingBill = {
  id: string; tenantName: string; tenantSlug: string;
  adminEmail?: string;
  plan: string; planId: string; planPrice: number;
  billingPeriod: string; dueDate: string;
  status: string; isSuspended: boolean;
  hasStripeCustomer: boolean; hasStripeSubscription: boolean;
};

const formatUsd = (dollars: number) =>
  `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function AdminDashboard() {
  const { toast } = useToast();
  const { user: sessionUser } = useAuth();
  const branding = useBranding();

  // ── tab state (controlled so we can jump to billing after Stripe return) ──
  const [activeTab, setActiveTab] = useState("overview");

  // ── detect return from Stripe Checkout initiated by SA ─────────────────
  // When SA clicks Subscribe from Pending Bills, the success URL is
  // /admin?subscribed=1.  On return: switch to billing tab, toast, invalidate,
  // and poll once more after 5 s in case the webhook is slightly delayed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "1") {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("subscribed");
      window.history.replaceState({}, "", clean.toString());
      setActiveTab("billing");
      toast({
        title: "Payment received",
        description: "Subscription activated — billing data is refreshing.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/pending-bills"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing-tenants"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      // Second pass after 5 s to catch any webhook delivery delay.
      const tid = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/pending-bills"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/billing-tenants"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      }, 5000);
      return () => clearTimeout(tid);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── data ──────────────────────────────────────────────────────────────
  const { data: stats, isLoading } = useQuery<any>({ queryKey: ["/api/admin/stats"] });
  const { data: plans } = useQuery<Plan[]>({ queryKey: ["/api/admin/plans"] });
  const { data: masterAdmins } = useQuery<MasterAdmin[]>({ queryKey: ["/api/admin/master-admins"] });
  const { data: analytics } = useQuery<Analytics>({ queryKey: ["/api/admin/analytics"] });
  const { data: billingTenants, isLoading: billingLoading } = useQuery<BillingTenant[]>({
    queryKey: ["/api/admin/billing-tenants"],
  });
  const { data: pendingBills, isLoading: pendingBillsLoading } = useQuery<PendingBill[]>({
    queryKey: ["/api/admin/billing/pending-bills"],
  });
  const { data: conversations } = useQuery<any[]>({ queryKey: ["/api/admin/conversations"] });
  const { data: stripeStatus } = useQuery<{ connected: boolean }>({ queryKey: ["/api/admin/stripe/status"] });
  const [selectedConvo, setSelectedConvo] = useState<string | null>(null);
  const { data: convoMessages } = useQuery<any[]>({
    queryKey: ["/api/admin/conversations", selectedConvo, "messages"],
    enabled: !!selectedConvo,
  });

  const [orgSearch, setOrgSearch] = useState("");
  const [orgPlanFilter, setOrgPlanFilter] = useState<string>("__all__");
  const [userSearch, setUserSearch] = useState("");
  const [billingSearch, setBillingSearch] = useState("");
  const [convoSearch, setConvoSearch] = useState("");
  const [convoOrgFilter, setConvoOrgFilter] = useState<string>("__all__");

  // ── tenants view: "active" table or "deleted" archive ────────────────
  const [tenantsView, setTenantsView] = useState<"active" | "deleted">("active");

  // ── users modal ───────────────────────────────────────────────────────
  const [selectedUsersOrg, setSelectedUsersOrg] = useState<{ id: string; name: string } | null>(null);

  // ── deleted tab multi-select (restore + permanent delete) ───────────
  const [selectedDeletedIds, setSelectedDeletedIds] = useState<string[]>([]);
  const [bulkRestoreOpen, setBulkRestoreOpen] = useState(false);
  const toggleDeletedSelection = (id: string) =>
    setSelectedDeletedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const bulkRestoreMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => apiRequest("POST", `/api/admin/organizations/${id}/restore`)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      queryClient.invalidateQueries({ queryKey: ["/api/audit", "deleted-orgs"] });
      setSelectedDeletedIds([]);
      setBulkRestoreOpen(false);
      toast({ title: `${selectedDeletedIds.length} tenant${selectedDeletedIds.length !== 1 ? "s" : ""} restored` });
    },
    onError: (err: Error) => toast({ title: "Restore failed", description: err.message, variant: "destructive" }),
  });

  // ── permanent delete (single) ─────────────────────────────────────────
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [permanentDeleteConfirmName, setPermanentDeleteConfirmName] = useState("");
  const permanentDeleteMutation = useMutation({
    mutationFn: async ({ id, confirmName }: { id: string; confirmName: string }) => {
      const res = await apiRequest("DELETE", `/api/admin/organizations/${id}/permanent`, { confirmName });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Permanent delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/organizations/archived"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      setPermanentDeleteTarget(null);
      setPermanentDeleteConfirmName("");
      toast({ title: "Tenant permanently deleted", description: "This action cannot be undone." });
    },
    onError: (err: Error) => toast({ title: "Permanent delete failed", description: err.message, variant: "destructive" }),
  });

  // ── permanent delete (bulk) ───────────────────────────────────────────
  const [bulkPermanentDeleteOpen, setBulkPermanentDeleteOpen] = useState(false);
  const bulkPermanentDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await apiRequest("POST", "/api/admin/organizations/bulk-permanent-delete", { ids });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Bulk permanent delete failed");
      }
      return res.json() as Promise<{ deletedCount: number; deletedTenants: any[]; protectedTenants: any[] }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/organizations/archived"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      const deletedIds = new Set(data.deletedTenants.map((t: any) => t.id));
      setSelectedDeletedIds((prev) => prev.filter((id) => !deletedIds.has(id)));
      setBulkPermanentDeleteOpen(false);
      if (data.deletedCount > 0)
        toast({ title: `${data.deletedCount} tenant${data.deletedCount !== 1 ? "s" : ""} permanently deleted` });
      if (data.protectedTenants.length > 0) {
        const reasons = data.protectedTenants
          .map((t: any) => `${t.name}: ${t.reason ?? "unknown error"}`)
          .join("; ");
        toast({
          title: `${data.protectedTenants.length} tenant${data.protectedTenants.length !== 1 ? "s" : ""} could not be deleted`,
          description: reasons,
          variant: "destructive",
        });
      }
    },
    onError: (err: Error) => toast({ title: "Bulk permanent delete failed", description: err.message, variant: "destructive" }),
  });

  // ── tenant CSV download ───────────────────────────────────────────────
  const [tenantCsvPending, setTenantCsvPending] = useState(false);
  async function downloadTenantCsv() {
    if (tenantCsvPending) return;
    setTenantCsvPending(true);
    try {
      const token = getAuthToken();
      const res = await fetch("/api/admin/reports/tenants.csv", {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(
          res.status === 401 || res.status === 403
            ? "You don't have permission to download this report."
            : detail || `Download failed (${res.status})`
        );
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Use the filename from Content-Disposition if provided
      const cd = res.headers.get("content-disposition") ?? "";
      const match = cd.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i);
      a.download = match ? match[1].trim() : "tenants.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Report downloaded", description: "Tenant CSV saved to your downloads folder." });
    } catch (err: any) {
      toast({ title: "Download failed", description: err.message ?? "Could not download the report.", variant: "destructive" });
    } finally {
      setTenantCsvPending(false);
    }
  }

  // Query for archived (soft-deleted) orgs directly from the Organizations table.
  // Previously this used the audit-log endpoint which preserved entries even after
  // a permanent delete — meaning deleted tenants would keep reappearing.
  // The new endpoint queries ArchivedAt IS NOT NULL; once HardDelete removes the
  // Organization row the entry disappears here automatically.
  const { data: deletedOrgAudits = [], isLoading: deletedOrgsLoading } = useQuery({
    queryKey: ["/api/admin/organizations/archived"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/organizations/archived");
      if (!res.ok) return [];
      return res.json();
    },
    enabled: tenantsView === "deleted",
  }) as { data: any[]; isLoading: boolean };

  // ── tenant selection (bulk delete) ───────────────────────────────────
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const toggleOrgSelection = (id: string) =>
    setSelectedOrgIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // ── mutations: tenants ────────────────────────────────────────────────
  const updateOrgMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/organizations/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({ title: "Tenant updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // Restore one or more archived tenants (POST /api/admin/organizations/{id}/restore)
  // and refresh the same admin list keys that archiving invalidated.
  const restoreOrgs = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => apiRequest("POST", `/api/admin/organizations/${id}/restore`)));
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({ title: ids.length > 1 ? `${ids.length} tenants restored` : "Tenant restored" });
    } catch (err: any) {
      toast({ title: "Restore failed", description: err?.message, variant: "destructive" });
    }
  };

  const deleteOrgMutation = useMutation({
    mutationFn: async ({ id, confirmName }: { id: string; confirmName: string }) => {
      await apiRequest("DELETE", `/api/admin/organizations/${id}`, { confirmName });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing-tenants"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/pending-bills"] });
      const archivedId = variables.id;
      toast({
        title: "Tenant archived",
        description: "Its audit trail and history are retained.",
        action: (
          <ToastAction altText={`Undo — restore tenant "${variables.confirmName}"`} onClick={() => restoreOrgs([archivedId])} data-testid="button-undo-archive-org">
            Undo
          </ToastAction>
        ),
      });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const [protectedWarning, setProtectedWarning] = useState<{ name: string; reason: string }[]>([]);
  const [protectedWarningOpen, setProtectedWarningOpen] = useState(false);

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await apiRequest("POST", "/api/admin/organizations/bulk-delete", { ids });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Bulk delete failed");
      }
      return res.json() as Promise<{
        deletedCount: number;
        deletedTenants: { id: string; name: string }[];
        protectedTenants: { id: string; name: string; reason: string }[];
      }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing-tenants"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/pending-bills"] });
      // Remove only the successfully deleted ones from selection
      const deletedIds = new Set(data.deletedTenants.map((t) => t.id));
      setSelectedOrgIds((prev) => prev.filter((id) => !deletedIds.has(id)));
      setBulkDeleteOpen(false);
      if (data.deletedCount > 0) {
        // Capture the archived IDs before local state changes so Undo can
        // restore exactly what was archived.
        const archivedIds = data.deletedTenants.map((t) => t.id);
        toast({
          title: `${data.deletedCount} tenant${data.deletedCount > 1 ? "s" : ""} archived`,
          description: "Their audit trails and history are retained.",
          action: (
            <ToastAction altText={`Undo — restore ${archivedIds.length} archived tenant${archivedIds.length > 1 ? "s" : ""}`} onClick={() => restoreOrgs(archivedIds)} data-testid="button-undo-bulk-archive-orgs">
              Undo
            </ToastAction>
          ),
        });
      }
      if (data.protectedTenants.length > 0) {
        setProtectedWarning(data.protectedTenants);
        setProtectedWarningOpen(true);
      }
    },
    onError: (err: Error) => {
      // Show the backend's actual message when present; only fall back to
      // generic copy when there is truly no message from the server.
      const description = err.message && err.message !== "Bulk delete failed"
        ? err.message
        : "Unable to archive tenants. Please try again.";
      toast({ title: "Archive failed", description, variant: "destructive" });
    },
  });

  const suspendOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/organizations/${id}/suspend`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: "Tenant suspended", description: "All active sessions have been invalidated." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const unsuspendOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/organizations/${id}/unsuspend`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: "Tenant unsuspended", description: "Users must log in again to obtain new sessions." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const createOrgMutation = useMutation({
    mutationFn: async (data: { name: string; slug: string; plan?: string; adminEmail?: string; adminPassword?: string; adminName?: string }) => {
      const res = await apiRequest("POST", "/api/admin/organizations", data);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      // Refresh billing cards so a new paid-plan tenant appears immediately
      // in Pending Billing without requiring a manual page refresh.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing/pending-bills"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/billing-tenants"] });
      toast({
        title: "Tenant created",
        description: data?.admin?.email ? `Org admin ${data.admin.email} provisioned.` : undefined,
      });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // ── mutations: users ──────────────────────────────────────────────────
  const createTenantUserMutation = useMutation({
    mutationFn: async (data: { email: string; name: string; password: string; role: string; organizationId: string }) => {
      const res = await apiRequest("POST", "/api/admin/users", data);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to create user");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({ title: "User created", description: "The new user has been created and linked to the selected organization." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/users/${id}`, { role });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/master-admins"] });
      toast({ title: "User role updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/admin/users/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      // Tenant userCount is derived in analytics — keep it coherent.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({ title: "User deactivated", description: "Their access is revoked and active sessions are signed out." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // ── mutations: master admins ──────────────────────────────────────────
  const createMasterMutation = useMutation({
    mutationFn: async (data: { email: string; name: string; password: string }) => {
      const res = await apiRequest("POST", "/api/admin/master-admins", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/master-admins"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: "Master admin created" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const toggleMasterActiveMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/master-admins/${id}`, { active: String(active) });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/master-admins"] });
      // The Users tab pulls from /api/admin/stats which carries the active
      // flag — keep it coherent.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: "Master admin updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMasterMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/admin/master-admins/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/master-admins"] });
      // /stats lists every user — needs to forget the deleted row too.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: "Master admin removed" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // ── mutations: billing reminder ───────────────────────────────────────
  const [reminderPending, setReminderPending] = useState<string | null>(null);
  const sendReminderMutation = useMutation({
    mutationFn: async (orgId: string) => {
      setReminderPending(orgId);
      const res = await apiRequest("POST", `/api/admin/billing-reminder/${orgId}`);
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Reminder sent", description: data?.message ?? "Email delivered to org admin." });
    },
    onError: (err: Error) => toast({ title: "Reminder failed", description: err.message, variant: "destructive" }),
    onSettled: () => setReminderPending(null),
  });

  // ── mutations: send payment link ──────────────────────────────────────
  // Generates a Stripe-hosted URL (Portal or Checkout depending on whether
  // the org has a StripeCustomerId) and emails it to the org admin.
  const [paymentLinkPending, setPaymentLinkPending] = useState<string | null>(null);
  const sendPaymentLinkMutation = useMutation({
    mutationFn: async (orgId: string) => {
      setPaymentLinkPending(orgId);
      const res = await apiRequest("POST", `/api/admin/billing/send-payment-link/${orgId}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message ?? "Failed to send payment link");
      return body;
    },
    onSuccess: (data: any) => {
      toast({ title: "Payment link sent", description: data?.message ?? "Email delivered to org admin." });
    },
    onError: (err: Error) => toast({ title: "Payment link failed", description: err.message, variant: "destructive" }),
    onSettled: () => setPaymentLinkPending(null),
  });

  // ── mutations: plans ──────────────────────────────────────────────────
  // Plan price drives MRR in /analytics — every plan mutation invalidates
  // both /plans and /analytics so the Billing tab stays in sync.
  const invalidateAfterPlanChange = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
  };

  const createPlanMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/plans", data);
      return res.json();
    },
    onSuccess: (body: any) => {
      invalidateAfterPlanChange();
      // Backend returns either the bare plan or { plan, warning } when
      // Stripe sync was attempted but failed. Surface the warning so the
      // master admin knows the local plan saved but checkout won't work
      // until Stripe is reconnected.
      if (body?.warning) toast({ title: "Plan created — Stripe sync issue", description: body.warning, variant: "destructive" });
      else toast({ title: "Plan created", description: "Synced to Stripe." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/plans/${id}`, data);
      return res.json();
    },
    onSuccess: (body: any) => {
      invalidateAfterPlanChange();
      if (body?.warning) toast({ title: "Plan updated — Stripe sync issue", description: body.warning, variant: "destructive" });
      else toast({ title: "Plan updated", description: "Synced to Stripe." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deletePlanMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/admin/plans/${id}`);
      // 204 has no body — guard the .json() call
      return res.status === 204 ? null : res.json();
    },
    onSuccess: () => {
      invalidateAfterPlanChange();
      toast({ title: "Plan removed" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // ── mutations: Stripe subscription lifecycle ──────────────────────────
  // Both endpoints return a Stripe-hosted URL — we redirect the browser
  // to it instead of trying to render Stripe's UI in-app.
  const subscribeMutation = useMutation({
    mutationFn: async ({ orgId, planId, successUrl, cancelUrl }: {
      orgId: string; planId: string; successUrl?: string; cancelUrl?: string;
    }) => {
      const res = await apiRequest("POST", `/api/admin/organizations/${orgId}/subscribe`, {
        planId, successUrl, cancelUrl,
      });
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: Error) => toast({ title: "Stripe subscribe failed", description: err.message, variant: "destructive" }),
  });

  const billingPortalMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest("POST", `/api/admin/organizations/${orgId}/billing-portal`);
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => {
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    },
    onError: (err: Error) => toast({ title: "Billing portal failed", description: err.message, variant: "destructive" }),
  });

  // ── Tenant Impersonation ──────────────────────────────────────────────
  // Platform Admin only. Issues a scoped JWT for the target org's OrgAdmin,
  // then opens the tenant dashboard in a NEW tab (sessionStorage-isolated so
  // the admin's own token in localStorage is never touched).
  const [impersonateTarget, setImpersonateTarget] = useState<OrgRow | null>(null);
  const impersonateMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest("POST", `/api/admin/organizations/${orgId}/impersonate`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? "Impersonation failed");
      }
      return res.json() as Promise<{
        token: string;
        expiresAt: string;
        targetUser: { id: string; email: string; name: string; role: string };
        organization: { id: string; name: string; slug: string };
      }>;
    },
    onSuccess: (data) => {
      setImpersonateTarget(null);
      // Open a new tab at the handoff route. The token is in the hash fragment
      // (never sent to the server) and is written to sessionStorage there.
      const url = `/impersonate#t=${encodeURIComponent(data.token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast({
        title: "Impersonation started",
        description: `Opened tenant session for ${data.organization.name} as ${data.targetUser.email}.`,
      });
    },
    onError: (err: Error) => {
      setImpersonateTarget(null);
      toast({ title: "Impersonation failed", description: err.message, variant: "destructive" });
    },
  });

  // ── derived ───────────────────────────────────────────────────────────
  const filteredOrgs = useMemo(() => {
    const q = orgSearch.toLowerCase();
    return (stats?.organizations ?? [])
      // Hide the platform org from the customer-facing list — it's an
      // internal bookkeeping container, not a tenant.
      .filter((o: any) => o.slug !== "platform")
      .filter((o: any) =>
        o.name.toLowerCase().includes(q) ||
        o.slug.toLowerCase().includes(q)
      )
      .filter((o: any) => orgPlanFilter === "__all__" || o.plan === orgPlanFilter)
      // Latest created first
      .sort((a: any, b: any) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
  }, [stats?.organizations, orgSearch, orgPlanFilter]);

  const filteredConversations = useMemo(() => {
    const q = convoSearch.toLowerCase();
    return (conversations ?? []).filter((c: any) => {
      if (convoOrgFilter !== "__all__" && c.organizationId !== convoOrgFilter) return false;
      if (!q) return true;
      return (
        (c.sessionId || "").toLowerCase().includes(q) ||
        (c.chatbot?.name || "").toLowerCase().includes(q) ||
        (c.userName || "").toLowerCase().includes(q) ||
        (c.userEmail || "").toLowerCase().includes(q) ||
        (c.channel || "").toLowerCase().includes(q)
      );
    });
  }, [conversations, convoSearch, convoOrgFilter]);

  const orgNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const o of stats?.organizations ?? []) map[o.id] = o.name;
    return map;
  }, [stats?.organizations]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    return (stats?.users ?? []).filter((u: any) =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.organizationName || orgNameById[u.organizationId] || "").toLowerCase().includes(q)
    );
  }, [stats?.users, userSearch, orgNameById]);

  const topTenants = useMemo(() => {
    return [...(analytics?.tenants ?? [])]
      .sort((a, b) => b.messagesUsed - a.messagesUsed)
      .slice(0, 5);
  }, [analytics?.tenants]);

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6" aria-busy="true">
        <p className="sr-only" role="status">Loading admin dashboard…</p>
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  const mrr = analytics?.summary?.monthlyRecurringRevenueCents ?? 0;

  return (
    <PageShell
      eyebrow={<span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" />Internal · Cross-tenant</span>}
      titleTestId="text-admin-title"
      title={
        <span className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-500" />
          Master Admin Console
        </span>
      }
      subtitle={`Manage every tenant, plan and platform admin. Powered by ${branding.poweredBy}.`}
      actions={
        <Link href="/admin/branding">
          <Button variant="outline" size="sm" data-testid="link-admin-branding">
            <Sparkles className="w-4 h-4 mr-2" />Branding
          </Button>
        </Link>
      }
      stats={
        <>
          <PageStat label="Organizations" value={<span data-testid="text-stat-orgs">{stats?.totalOrganizations ?? 0}</span>} hint="Active tenants" icon={<Building2 className="w-4 h-4" />} />
          <PageStat label="Users" value={<span data-testid="text-stat-users">{stats?.totalUsers ?? 0}</span>} hint="Across every tenant" icon={<Users className="w-4 h-4" />} />
          <PageStat label="Total bots" value={<span data-testid="text-stat-bots">{stats?.totalChatbots ?? 0}</span>} hint="Configured chatbots" icon={<Bot className="w-4 h-4" />} />
          <PageStat label="Conversations" value={<span data-testid="text-stat-convos">{stats?.totalConversations ?? 0}</span>} hint="Total sessions handled" icon={<MessageSquare className="w-4 h-4" />} />
        </>
      }
    >
      {/* Hidden span block keeps legacy ratings test-ids alive for tests. */}
      <div className="hidden">
        <span data-testid="text-stat-ratings">{stats?.totalRatings ?? 0}</span>
        <span data-testid="text-stat-avg-rating">{stats?.averageRating ?? 0}</span>
        <Badge variant="outline" data-testid="badge-internal"><Lock className="w-3 h-3" />Internal</Badge>
      </div>

      {/* Quick-jump tile row — colourful entry points to the heaviest tabs. */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <a href="#tenants-tab" onClick={(e) => { e.preventDefault(); document.querySelector<HTMLElement>('[data-testid="tab-organizations"]')?.click(); }} className="block">
          <div className="feature-tile h-full" data-testid="quick-jump-tenants">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
              <Building2 className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Tenants</div>
            <p className="text-xs text-muted-foreground mt-1">Provision, suspend, and inspect every customer org.</p>
          </div>
        </a>
        <a href="#plans-tab" onClick={(e) => { e.preventDefault(); document.querySelector<HTMLElement>('[data-testid="tab-plans"]')?.click(); }} className="block">
          <div className="feature-tile h-full" data-testid="quick-jump-plans">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(262 70% 60%), hsl(280 70% 55%))" }}>
              <Package className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Plans & pricing</div>
            <p className="text-xs text-muted-foreground mt-1">Edit subscriptions, sync prices to Stripe.</p>
          </div>
        </a>
        <a href="#billing-tab" onClick={(e) => { e.preventDefault(); document.querySelector<HTMLElement>('[data-testid="tab-billing"]')?.click(); }} className="block">
          <div className="feature-tile h-full" data-testid="quick-jump-billing">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(28 92% 48%), hsl(18 92% 42%))" }}>
              <DollarSign className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">{formatUsd(mrr)} MRR</div>
            <p className="text-xs text-muted-foreground mt-1">Live recurring revenue across all tenants.</p>
          </div>
        </a>
        <a href="#masters-tab" onClick={(e) => { e.preventDefault(); document.querySelector<HTMLElement>('[data-testid="tab-masters"]')?.click(); }} className="block">
          <div className="feature-tile h-full" data-testid="quick-jump-masters">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(340 80% 60%), hsl(320 75% 55%))" }}>
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Master admins</div>
            <p className="text-xs text-muted-foreground mt-1">Add or revoke platform-wide super admins.</p>
          </div>
        </a>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><BarChart3 className="w-4 h-4 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="organizations" data-testid="tab-organizations"><Building2 className="w-4 h-4 mr-1" />Tenants</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans"><Package className="w-4 h-4 mr-1" />Plans</TabsTrigger>
          <TabsTrigger value="masters" data-testid="tab-masters"><ShieldCheck className="w-4 h-4 mr-1" />Master Admins</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing"><DollarSign className="w-4 h-4 mr-1" />Billing</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><Download className="w-4 h-4 mr-1" />Reports</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1" />Users</TabsTrigger>
          <TabsTrigger value="conversations" data-testid="tab-conversations"><MessageSquare className="w-4 h-4 mr-1" />Conversations</TabsTrigger>
        </TabsList>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* OVERVIEW                                                        */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-sm">Top tenants by message usage</CardTitle></CardHeader>
            <CardContent>
              {topTenants.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No tenant activity yet.</p>
              ) : (
                <div className="space-y-2" data-testid="table-top-tenants">
                  {topTenants.map((t) => {
                    const pct = t.monthlyMessageLimit > 0 ? Math.min(100, Math.round((t.messagesUsed / t.monthlyMessageLimit) * 100)) : 0;
                    return (
                      <div key={t.id} className="flex items-center justify-between text-sm gap-4" data-testid={`row-top-tenant-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.plan ?? "no plan"} · {t.botCount} bots · {t.userCount} users</p>
                        </div>
                        <div className="w-32 text-right">
                          <p className="font-mono text-xs">{t.messagesUsed.toLocaleString()} / {t.monthlyMessageLimit.toLocaleString()}</p>
                          <div className="h-1.5 bg-muted rounded mt-1 overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* TENANTS                                                         */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="organizations" className="space-y-3 mt-4">

          {/* ── Toolbar ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Active / Archived sub-tab pills */}
              <div className="flex rounded-lg border overflow-hidden text-xs shrink-0">
                <button
                  onClick={() => setTenantsView("active")}
                  className={`flex items-center gap-1 px-3 py-1.5 font-medium transition-colors ${tenantsView === "active" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted text-muted-foreground"}`}
                  data-testid="tab-tenants-active"
                >
                  <Building2 className="w-3 h-3" />Active
                </button>
                <button
                  onClick={() => setTenantsView("deleted")}
                  className={`flex items-center gap-1 px-3 py-1.5 font-medium transition-colors border-l ${tenantsView === "deleted" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted text-muted-foreground"}`}
                  data-testid="tab-tenants-deleted"
                >
                  <History className="w-3 h-3" />Deleted
                </button>
              </div>
              {tenantsView === "active" && (
                <>
                  <div className="relative min-w-[200px] flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search tenants…" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} className="pl-9" aria-label="Search tenants" data-testid="input-search-orgs" />
                  </div>
                  <Select value={orgPlanFilter} onValueChange={setOrgPlanFilter}>
                    <SelectTrigger className="w-[140px] h-9 text-xs" aria-label="Filter by plan" data-testid="select-filter-plan"><SelectValue placeholder="All plans" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All plans</SelectItem>
                      {(plans ?? []).map((p) => <SelectItem key={p.id} value={p.slug}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
            {tenantsView === "active" && (
              <CreateTenantDialog plans={plans ?? []} onCreate={(d) => createOrgMutation.mutate(d)} pending={createOrgMutation.isPending} />
            )}
          </div>

          {/* ── Bulk-selection action bar (active tenants) ──────────────── */}
          {tenantsView === "active" && selectedOrgIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2" data-testid="selection-action-bar">
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{selectedOrgIds.length} tenant{selectedOrgIds.length > 1 ? "s" : ""}</span> selected
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setSelectedOrgIds(filteredOrgs.map((o: any) => o.id))} data-testid="button-select-all-orgs">
                  Select all {filteredOrgs.length}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedOrgIds([])} data-testid="button-cancel-selection">
                  <X className="w-3 h-3 mr-1" />Cancel
                </Button>
                <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => setBulkDeleteOpen(true)} data-testid="button-bulk-delete-orgs">
                  <Trash2 className="w-3 h-3 mr-1" />Delete Selected ({selectedOrgIds.length})
                </Button>
              </div>
            </div>
          )}

          {/* ── Bulk-selection action bar (deleted tenants → restore) ───── */}
          {tenantsView === "deleted" && selectedDeletedIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2" data-testid="selection-action-bar-deleted">
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{selectedDeletedIds.length} tenant{selectedDeletedIds.length > 1 ? "s" : ""}</span> selected
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setSelectedDeletedIds(deletedOrgAudits.map((e: any) => e.entityId))} data-testid="button-select-all-deleted-orgs">
                  Select all {deletedOrgAudits.length}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedDeletedIds([])}>
                  <X className="w-3 h-3 mr-1" />Cancel
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-950" onClick={() => setBulkRestoreOpen(true)} data-testid="button-bulk-restore-orgs">
                  <RotateCcw className="w-3 h-3 mr-1" />Restore Selected ({selectedDeletedIds.length})
                </Button>
                <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => setBulkPermanentDeleteOpen(true)} data-testid="button-bulk-permanent-delete-orgs">
                  <Trash2 className="w-3 h-3 mr-1" />Permanently Delete ({selectedDeletedIds.length})
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* ACTIVE TENANTS TABLE                                           */}
          {/* Columns: Tenant | Status | Plan | Usage | Users | SMTP | Compliance | Actions */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {tenantsView === "active" && (
            <Card>
              <div className="overflow-x-auto">
                {/* min-w keeps all columns at their declared widths; horizontal scroll kicks in on narrow viewports */}
                <table className="w-full min-w-[1220px] text-sm border-collapse">
                  <colgroup>
                    <col className="w-[210px]" />  {/* Tenant */}
                    <col className="w-[100px]" />  {/* Status */}
                    <col className="w-[130px]" />  {/* Plan */}
                    <col className="w-[130px]" />  {/* Usage */}
                    <col className="w-[70px]" />   {/* Users */}
                    <col className="w-[120px]" />  {/* AI Provider */}
                    <col className="w-[130px]" />  {/* SMTP */}
                    <col className="w-[110px]" />  {/* Compliance */}
                    <col className="w-[220px]" />  {/* Actions — fixed width, no wrapping */}
                  </colgroup>
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Tenant</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Status</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Plan</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Usage</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Users</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">AI Provider</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">SMTP</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Compliance</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredOrgs.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center text-muted-foreground py-12 text-sm">
                          <Building2 className="w-9 h-9 mx-auto mb-2 opacity-25" />
                          <p className="font-medium">No tenants found</p>
                          <p className="text-xs mt-1">Try adjusting your search or plan filter</p>
                        </td>
                      </tr>
                    )}
                    {filteredOrgs.map((org: any) => {
                      const subStatus = org.subscriptionStatus as string | null | undefined;
                      const planRow = plans?.find((p) => p.slug === org.plan);
                      const planSyncedToStripe = !!planRow?.stripePriceId;
                      const planHasCompliance = (planRow?.features ?? []).some(
                        (f: string) => f === "compliance" || f.startsWith("compliance.framework.")
                      );
                      const stripeOn = stripeStatus?.connected !== false;
                      const isSelected = selectedOrgIds.includes(org.id);
                      const isActive = subStatus === "active" || subStatus === "trialing";
                      const usagePct = org.monthlyMessageLimit > 0
                        ? Math.min(100, Math.round(((org.messagesUsed ?? 0) / org.monthlyMessageLimit) * 100))
                        : 0;
                      return (
                        <tr
                          key={org.id}
                          data-testid={`card-org-${org.id}`}
                          onClick={() => toggleOrgSelection(org.id)}
                          className={`cursor-pointer align-middle transition-colors ${isSelected ? "bg-primary/[0.07] ring-1 ring-inset ring-primary/20 hover:bg-primary/[0.10]" : "hover:bg-muted/50"}`}
                        >
                          {/* ── Tenant name + slug ─────────────────────── */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold select-none ${tenantAvatarColor(org.name)}`}>
                                {tenantInitials(org.name)}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm leading-snug truncate" data-testid={`text-org-name-${org.id}`}>{org.name}</p>
                                <span className="text-[10px] text-muted-foreground/70 font-mono">/{org.slug}</span>
                              </div>
                            </div>
                          </td>

                          {/* ── Status ─────────────────────────────────── */}
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col items-center gap-1">
                              {subStatus ? (
                                <Badge
                                  variant={isActive ? "default" : "secondary"}
                                  className="text-[10px] capitalize"
                                  data-testid={`badge-sub-status-${org.id}`}
                                >{subStatus.replace(/_/g, " ")}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">no billing</Badge>
                              )}
                              {org.isSuspended && (
                                <Badge variant="destructive" className="text-[10px]">Suspended</Badge>
                              )}
                            </div>
                          </td>

                          {/* ── Plan selector ──────────────────────────── */}
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={org.plan}
                              onValueChange={(val) => {
                                const pRow = plans?.find((p) => p.slug === val);
                                const data: any = { plan: val };
                                if (pRow) {
                                  data.botLimit = pRow.botLimit;
                                  data.agentLimit = pRow.agentLimit ?? 2;
                                  data.monthlyMessageLimit = pRow.monthlyMessages;
                                }
                                updateOrgMutation.mutate({ id: org.id, data });
                              }}
                            >
                              <SelectTrigger className="w-[125px] h-7 text-xs" aria-label={`Plan for ${org.name}`} data-testid={`select-plan-${org.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(plans ?? []).map((p) => (
                                  <SelectItem key={p.id} value={p.slug}>{p.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>

                          {/* ── Usage: messages + mini bar ─────────────── */}
                          <td className="px-3 py-3">
                            <p className="font-mono text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                              {(org.messagesUsed ?? 0).toLocaleString()}<span className="opacity-40 mx-0.5">/</span>{org.monthlyMessageLimit.toLocaleString()}
                            </p>
                            <div className="w-20 h-1.5 bg-muted rounded-full mt-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${usagePct >= 90 ? "bg-destructive" : usagePct >= 70 ? "bg-amber-500" : "bg-primary"}`}
                                style={{ width: `${usagePct}%` }}
                              />
                            </div>
                          </td>

                          {/* ── Users: count (clickable → modal) ──────── */}
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const count = (stats?.users ?? []).filter((u: any) => u.organizationId === org.id).length;
                              return (
                                <button
                                  className="text-sm font-medium tabular-nums underline decoration-dotted underline-offset-2 cursor-pointer hover:text-primary transition-colors"
                                  onClick={() => setSelectedUsersOrg({ id: org.id, name: org.name })}
                                  title={`View ${count} user${count !== 1 ? "s" : ""} in ${org.name}`}
                                  data-testid={`button-view-users-${org.id}`}
                                >
                                  {count}
                                </button>
                              );
                            })()}
                          </td>

                          {/* ── AI Provider: configured status ─────────── */}
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full shrink-0 ${org.aiProviderConfigured ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
                              <span className={`text-xs ${org.aiProviderConfigured ? "text-emerald-700 dark:text-emerald-400 font-medium" : "text-muted-foreground"}`}>
                                {org.aiProviderConfigured ? "Active" : "Not configured"}
                              </span>
                            </div>
                          </td>

                          {/* ── SMTP: status dot + label + configure btn ── */}
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${org.smtpEnabled ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
                                <span className={`text-xs ${org.smtpEnabled ? "text-emerald-700 dark:text-emerald-400 font-medium" : "text-muted-foreground"}`}>
                                  {org.smtpEnabled ? "Configured" : "Not configured"}
                                </span>
                              </div>
                              <TenantSmtpDialog org={org} />
                            </div>
                          </td>

                          {/* ── Compliance (dedicated column) ──────────── */}
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            {planHasCompliance
                              ? <ComplianceOverrideDialog org={org} />
                              : <span className="text-xs text-muted-foreground/40">—</span>
                            }
                          </td>

                          {/* ── Actions: Edit · Delete · Suspend · Impersonate · [one of: Activate | Subscribe | Portal] ── */}
                          <td className="px-3 py-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1">

                              {/* 1. Edit */}
                              <EditTenantDialog
                                org={org}
                                onSave={(id, data) => updateOrgMutation.mutate({ id, data })}
                                pending={updateOrgMutation.isPending}
                              />

                              {/* 2. Delete */}
                              <ConfirmDialog
                                title={`Delete tenant "${org.name}"?`}
                                description={`This will remove "${org.name}" from active tenant listings. Tenant data, audit history, and records will be retained according to system policy. Type the tenant name to confirm.`}
                                confirmLabel="Delete tenant"
                                confirmTestId={`button-confirm-delete-org-${org.id}`}
                                requiredConfirmText={org.name}
                                onConfirm={() => deleteOrgMutation.mutate({ id: org.id, confirmName: org.name })}
                                trigger={
                                  <IconButton
                                    variant="ghost"
                                    label={`Delete ${org.name}`}
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    data-testid={`button-delete-org-${org.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                                  </IconButton>
                                }
                              />

                              {/* 3. Suspend / Unsuspend */}
                              {org.isSuspended ? (
                                <ConfirmDialog
                                  title={`Unsuspend "${org.name}"?`}
                                  description="Users will be able to log in again but must obtain fresh sessions."
                                  confirmLabel="Unsuspend"
                                  destructive={false}
                                  confirmTestId={`button-confirm-unsuspend-org-${org.id}`}
                                  onConfirm={() => unsuspendOrgMutation.mutate(org.id)}
                                  trigger={
                                    <IconButton
                                      variant="ghost"
                                      label={`Unsuspend ${org.name}`}
                                      className="h-8 w-8 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                                      data-testid={`button-unsuspend-org-${org.id}`}
                                    >
                                      <Power className="w-4 h-4" aria-hidden="true" />
                                    </IconButton>
                                  }
                                />
                              ) : (
                                <ConfirmDialog
                                  title={`Suspend "${org.name}"?`}
                                  description="All active sessions for this tenant will be immediately invalidated. Users must re-login after the suspension is lifted."
                                  confirmLabel="Suspend tenant"
                                  confirmTestId={`button-confirm-suspend-org-${org.id}`}
                                  onConfirm={() => suspendOrgMutation.mutate(org.id)}
                                  trigger={
                                    <IconButton
                                      variant="ghost"
                                      label={`Suspend ${org.name}`}
                                      className="h-8 w-8 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950"
                                      data-testid={`button-suspend-org-${org.id}`}
                                    >
                                      <Lock className="w-4 h-4" aria-hidden="true" />
                                    </IconButton>
                                  }
                                />
                              )}

                              {/* 4. Impersonate — IconButton already has its own Tooltip internally */}
                              <IconButton
                                variant="ghost"
                                label={`Impersonate — ${org.name}`}
                                className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950"
                                onClick={() => setImpersonateTarget(org)}
                                data-testid={`button-impersonate-${org.id}`}
                              >
                                <UserCheck className="w-4 h-4" aria-hidden="true" />
                              </IconButton>

                              {/* Mutually-exclusive billing action — exactly one of these appears:
                                  • Billing Portal  — tenant has a Stripe subscription (completed checkout)
                                  • Subscribe       — Stripe is enabled, plan is synced, but no subscription yet
                                  • Activate        — no Stripe at all; manual activation only
                                  Never show more than one at once. */}
                              {(() => {
                                const hasStripeCustomer     = !!org.stripeCustomerId;
                                const hasStripeSubscription = !!org.stripeSubscriptionId;

                                if (hasStripeCustomer && hasStripeSubscription) {
                                  return (
                                    <IconButton
                                      variant="ghost"
                                      label={`Billing Portal — ${org.name}`}
                                      className="h-8 w-8"
                                      disabled={billingPortalMutation.isPending}
                                      onClick={() => billingPortalMutation.mutate(org.id)}
                                      data-testid={`button-portal-${org.id}`}
                                    >
                                      <ExternalLink className="w-4 h-4" aria-hidden="true" />
                                    </IconButton>
                                  );
                                }

                                // Show Subscribe when either: Stripe is live + plan exists + no
                                // subscription + tenant is not already active, OR the tenant is
                                // explicitly pending on a paid Stripe plan (catches the case where
                                // stripeOn hasn't resolved yet but the DB state is unambiguous).
                                if ((stripeOn && planRow && !hasStripeSubscription && !isActive) ||
                                    (planSyncedToStripe && subStatus === "pending" && !hasStripeSubscription)) {
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 text-xs px-2"
                                          disabled={!planSyncedToStripe || subscribeMutation.isPending}
                                          onClick={() => subscribeMutation.mutate({
                                            orgId: org.id,
                                            planId: planRow.id,
                                            successUrl: `${window.location.origin}/admin?subscribed=1`,
                                            cancelUrl: `${window.location.origin}/admin`,
                                          })}
                                          data-testid={`button-subscribe-${org.id}`}
                                        >
                                          <CreditCard className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Subscribe
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>{planSyncedToStripe ? "Start Stripe Checkout" : "Plan not synced to Stripe yet"}</TooltipContent>
                                    </Tooltip>
                                  );
                                }

                                if (subStatus === "pending") {
                                  return (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 text-xs px-2 text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-950"
                                          onClick={() => updateOrgMutation.mutate({ id: org.id, data: { subscriptionStatus: "active" } })}
                                          data-testid={`button-activate-${org.id}`}
                                        >
                                          <CheckCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Activate
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Manually activate without Stripe</TooltipContent>
                                    </Tooltip>
                                  );
                                }

                                return null;
                              })()}

                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Table footer */}
              <div className="px-4 py-2 border-t flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {filteredOrgs.length} tenant{filteredOrgs.length !== 1 ? "s" : ""}
                  {orgSearch || orgPlanFilter !== "__all__" ? " matching filters" : " total"} · Newest first
                </span>
                {selectedOrgIds.length > 0
                  ? <span className="text-primary font-medium">{selectedOrgIds.length} selected — click row to deselect</span>
                  : filteredOrgs.length > 0
                    ? <span className="opacity-50">Click any row to enter selection mode</span>
                    : null
                }
              </div>
            </Card>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* ARCHIVED / DELETED TENANTS TABLE                               */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {tenantsView === "deleted" && (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Tenant</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Deleted at</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Deleted by</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Audit</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {deletedOrgsLoading && (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                          Loading deleted tenants…
                        </td>
                      </tr>
                    )}
                    {!deletedOrgsLoading && deletedOrgAudits.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                          <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          No deleted tenants found
                        </td>
                      </tr>
                    )}
                    {deletedOrgAudits.map((entry: any) => {
                      // New endpoint returns org rows directly (id, name, archivedAt, archivedByUserId).
                      const deletedAt = entry.archivedAt ? new Date(entry.archivedAt) : null;
                      const orgId = entry.id ?? entry.entityId;
                      const orgName = entry.name ?? entry.entityName ?? entry.organizationName ?? orgId;
                      const actorUserId = entry.archivedByUserId ?? entry.userId;
                      const isSelected = selectedDeletedIds.includes(orgId);
                      const deletedByUser = [
                        ...(masterAdmins ?? []),
                        ...(stats?.users ?? []),
                      ].find((u: any) => u.id === actorUserId);
                      const deletedByLabel = deletedByUser
                        ? (deletedByUser.name || deletedByUser.email)
                        : (actorUserId ? `User ${actorUserId.slice(0, 8)}…` : "System");
                      return (
                        <tr
                          key={entry.id ?? orgId}
                          className={`cursor-pointer align-middle transition-colors ${isSelected ? "bg-primary/[0.07] ring-1 ring-inset ring-primary/20 hover:bg-primary/[0.10]" : "hover:bg-muted/50"}`}
                          onClick={() => toggleDeletedSelection(orgId)}
                        >
                          {/* Tenant name */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold select-none opacity-50 ${tenantAvatarColor(orgName)}`}>
                                {tenantInitials(orgName)}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm leading-snug truncate">{orgName}</p>
                                <span className="text-[10px] font-mono text-destructive/70 bg-destructive/10 px-1.5 py-px rounded-sm mt-0.5 inline-block">deleted</span>
                              </div>
                            </div>
                          </td>
                          {/* Deleted date */}
                          <td className="px-3 py-3 text-center text-xs text-muted-foreground">
                            {deletedAt ? (
                              <>
                                <p>{deletedAt.toLocaleDateString()}</p>
                                <p className="text-[10px]">{deletedAt.toLocaleTimeString()}</p>
                              </>
                            ) : "—"}
                          </td>
                          {/* Deleted by */}
                          <td className="px-3 py-3 text-center text-xs text-muted-foreground">
                            {deletedByLabel}
                          </td>
                          {/* Audit link */}
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <Link href="/admin/audit">
                              <IconButton variant="ghost" label="View audit log" className="h-7 w-7">
                                <History className="w-3.5 h-3.5" aria-hidden="true" />
                              </IconButton>
                            </Link>
                          </td>
                          {/* Restore + Permanent Delete actions */}
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => restoreOrgs([orgId])}
                                data-testid={`button-restore-org-${orgId}`}
                              >
                                <RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Restore
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => { setPermanentDeleteTarget({ id: orgId, name: orgName }); setPermanentDeleteConfirmName(""); }}
                                data-testid={`button-permanent-delete-org-${orgId}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 border-t flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {deletedOrgAudits.length} archived tenant{deletedOrgAudits.length !== 1 ? "s" : ""} · Use Restore to reactivate or Delete to remove permanently
                </span>
                {selectedDeletedIds.length > 0
                  ? <span className="text-primary font-medium">{selectedDeletedIds.length} selected — click row to deselect</span>
                  : deletedOrgAudits.length > 0
                    ? <span className="opacity-50">Click any row to enter selection mode</span>
                    : null
                }
              </div>
            </Card>
          )}

          {/* ── Bulk-delete confirmation dialog (portal, no z-index overlap) ── */}
          {(() => {
            const selOrgs = (stats?.organizations ?? []).filter((o: any) => selectedOrgIds.includes(o.id));
            return (
              <Dialog open={bulkDeleteOpen} onOpenChange={(v) => { if (!bulkDeleteMutation.isPending) setBulkDeleteOpen(v); }}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {selectedOrgIds.length > 1
                        ? `Archive Selected Tenants (${selectedOrgIds.length})`
                        : "Archive Selected Tenant"}
                    </DialogTitle>
                    <DialogDescription>
                      You are about to archive the selected tenant{selectedOrgIds.length > 1 ? "s" : ""} along with their users and data. Their audit trails and history are retained, not erased.
                    </DialogDescription>
                  </DialogHeader>
                  <ul className="max-h-48 overflow-y-auto space-y-1 my-1">
                    {selOrgs.map((o: any) => (
                      <li key={o.id} className="flex items-center gap-2 text-sm">
                        <span className="text-emerald-500">✓</span>
                        <span className="font-medium">{o.name}</span>
                        <Badge variant="outline" className="text-[10px]">{o.plan}</Badge>
                      </li>
                    ))}
                  </ul>
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleteMutation.isPending}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={bulkDeleteMutation.isPending}
                      onClick={() => bulkDeleteMutation.mutate(selectedOrgIds)}
                      data-testid="button-confirm-bulk-delete-orgs"
                    >
                      {bulkDeleteMutation.isPending ? "Archiving…" : "Archive tenants"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            );
          })()}

          {/* ── Bulk-restore confirmation dialog (deleted tab) ────────────── */}
          <Dialog open={bulkRestoreOpen} onOpenChange={(v) => { if (!bulkRestoreMutation.isPending) setBulkRestoreOpen(v); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  Restore {selectedDeletedIds.length} Deleted Tenant{selectedDeletedIds.length !== 1 ? "s" : ""}?
                </DialogTitle>
                <DialogDescription>
                  The selected tenant{selectedDeletedIds.length !== 1 ? "s" : ""} will be restored and their users will regain access.
                  Audit history is already retained.
                </DialogDescription>
              </DialogHeader>
              <ul className="max-h-48 overflow-y-auto space-y-1 my-1">
                {deletedOrgAudits
                  .filter((e: any) => selectedDeletedIds.includes(e.entityId))
                  .map((e: any) => (
                    <li key={e.entityId} className="flex items-center gap-2 text-sm">
                      <span className="text-emerald-500">↩</span>
                      <span className="font-medium">{e.entityName ?? e.organizationName ?? e.entityId}</span>
                    </li>
                  ))}
              </ul>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setBulkRestoreOpen(false)} disabled={bulkRestoreMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  disabled={bulkRestoreMutation.isPending}
                  onClick={() => bulkRestoreMutation.mutate(selectedDeletedIds)}
                  data-testid="button-confirm-bulk-restore-orgs"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {bulkRestoreMutation.isPending ? "Restoring…" : `Restore ${selectedDeletedIds.length} tenant${selectedDeletedIds.length !== 1 ? "s" : ""}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ── Single permanent delete confirmation ──────────────────────── */}
          <Dialog open={!!permanentDeleteTarget} onOpenChange={(v) => { if (!v && !permanentDeleteMutation.isPending) { setPermanentDeleteTarget(null); setPermanentDeleteConfirmName(""); } }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-5 h-5" aria-hidden="true" />
                  Permanently Delete Tenant?
                </DialogTitle>
                <DialogDescription>
                  This will <strong>irreversibly remove</strong> all data for{" "}
                  <strong>{permanentDeleteTarget?.name}</strong> including users, bots, conversations,
                  compliance records, and settings. Audit logs are retained.{" "}
                  <strong>This cannot be undone.</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5 my-1">
                <Label htmlFor="permanent-delete-confirm" className="text-xs text-muted-foreground">
                  Type <strong className="text-foreground">{permanentDeleteTarget?.name}</strong> to confirm
                </Label>
                <Input
                  id="permanent-delete-confirm"
                  value={permanentDeleteConfirmName}
                  onChange={(e) => setPermanentDeleteConfirmName(e.target.value)}
                  placeholder={permanentDeleteTarget?.name ?? ""}
                  autoComplete="off"
                  data-testid="input-permanent-delete-confirm"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => { setPermanentDeleteTarget(null); setPermanentDeleteConfirmName(""); }} disabled={permanentDeleteMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={permanentDeleteConfirmName !== permanentDeleteTarget?.name || permanentDeleteMutation.isPending}
                  onClick={() => permanentDeleteTarget && permanentDeleteMutation.mutate({ id: permanentDeleteTarget.id, confirmName: permanentDeleteConfirmName })}
                  data-testid="button-confirm-permanent-delete-org"
                >
                  {permanentDeleteMutation.isPending ? "Deleting…" : "Permanently Delete"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ── Bulk permanent delete confirmation ────────────────────────── */}
          <Dialog open={bulkPermanentDeleteOpen} onOpenChange={(v) => { if (!bulkPermanentDeleteMutation.isPending) setBulkPermanentDeleteOpen(v); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-5 h-5" aria-hidden="true" />
                  Permanently Delete {selectedDeletedIds.length} Tenant{selectedDeletedIds.length !== 1 ? "s" : ""}?
                </DialogTitle>
                <DialogDescription>
                  This will <strong>irreversibly remove</strong> all data for the selected tenants.
                  Users, bots, conversations, compliance records, and settings will be erased.
                  Audit logs are retained. <strong>This cannot be undone.</strong>
                </DialogDescription>
              </DialogHeader>
              <ul className="max-h-40 overflow-y-auto space-y-1 my-1">
                {deletedOrgAudits
                  .filter((e: any) => selectedDeletedIds.includes(e.entityId))
                  .map((e: any) => (
                    <li key={e.entityId} className="flex items-center gap-2 text-sm">
                      <span className="text-destructive">✕</span>
                      <span className="font-medium">{e.entityName ?? e.organizationName ?? e.entityId}</span>
                    </li>
                  ))}
              </ul>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setBulkPermanentDeleteOpen(false)} disabled={bulkPermanentDeleteMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={bulkPermanentDeleteMutation.isPending}
                  onClick={() => bulkPermanentDeleteMutation.mutate(selectedDeletedIds)}
                  data-testid="button-confirm-bulk-permanent-delete-orgs"
                >
                  {bulkPermanentDeleteMutation.isPending ? "Deleting…" : `Permanently Delete ${selectedDeletedIds.length} tenant${selectedDeletedIds.length !== 1 ? "s" : ""}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ── Protected-tenants warning (shown after a partial bulk delete) ── */}
          <Dialog open={protectedWarningOpen} onOpenChange={setProtectedWarningOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Some tenants could not be archived</DialogTitle>
                <DialogDescription>
                  The following tenants are protected and were skipped:
                </DialogDescription>
              </DialogHeader>
              <ul className="space-y-2 my-1 max-h-48 overflow-y-auto">
                {protectedWarning.map((t) => (
                  <li key={t.name} className="flex items-start gap-2 text-sm">
                    <span className="text-destructive mt-0.5">✕</span>
                    <div>
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <DialogFooter>
                <Button onClick={() => { setProtectedWarningOpen(false); setProtectedWarning([]); }}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ── Users detail modal ───────────────────────────────────────── */}
          {selectedUsersOrg && (
            <TenantUsersModal
              orgId={selectedUsersOrg.id}
              orgName={selectedUsersOrg.name}
              open={!!selectedUsersOrg}
              onClose={() => setSelectedUsersOrg(null)}
            />
          )}
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* PLANS                                                           */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="plans" className="space-y-3 mt-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Subscription plans available to tenants. The "features" array drives plan-level capability gating.</p>
            <PlanDialog
              mode="create"
              onSubmit={(d) => createPlanMutation.mutate(d)}
              pending={createPlanMutation.isPending}
              triggerTestId="button-new-plan"
              triggerLabel="New plan"
            />
          </div>
          {(plans ?? []).map((p) => (
            <Card key={p.id} data-testid={`card-plan-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium" data-testid={`text-plan-name-${p.id}`}>{p.name}</p>
                    <Badge variant="outline" className="text-[10px]">/{p.slug}</Badge>
                    {p.isActive === "false" && <Badge variant="secondary" className="text-[10px]">inactive</Badge>}
                    {p.stripePriceId ? (
                      <Badge variant="default" className="text-[10px] bg-emerald-600 hover:bg-emerald-600" data-testid={`badge-stripe-synced-${p.id}`}>
                        <CreditCard className="w-3 h-3 mr-0.5" />Stripe
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`badge-stripe-unsynced-${p.id}`}>not in Stripe</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <PlanDialog
                      mode="edit"
                      initial={p}
                      onSubmit={(d) => updatePlanMutation.mutate({ id: p.id, data: d })}
                      pending={updatePlanMutation.isPending}
                      triggerTestId={`button-edit-plan-${p.id}`}
                      triggerLabel="Edit"
                    />
                    <ConfirmDialog
                      title={`Remove plan "${p.name}"?`}
                      description={`Tenants currently on "${p.name}" keep their applied limits, but the plan will no longer be offered. This cannot be undone.`}
                      confirmLabel="Remove plan"
                      confirmTestId={`button-confirm-delete-plan-${p.id}`}
                      onConfirm={() => deletePlanMutation.mutate(p.id)}
                      trigger={
                        <IconButton variant="ghost" label={`Delete plan ${p.name}`} className="h-8 w-8 text-destructive" data-testid={`button-delete-plan-${p.id}`}>
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </IconButton>
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
                  <div><span className="text-foreground font-mono">{formatUsd(p.price)}</span> /{p.billingInterval === "year" ? "yr" : "mo"}</div>
                  <div>Bots: <span className="text-foreground">{p.botLimit}</span></div>
                  <div>Agents: <span className="text-foreground">{p.agentLimit ?? 2}</span></div>
                  <div>Messages: <span className="text-foreground">{p.monthlyMessages.toLocaleString()}</span></div>
                </div>
                {(p.features?.length ?? 0) > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`features-plan-${p.id}`}>
                    {p.features!.map((f, i) => (
                      <Badge key={`${f}-${i}`} variant="secondary" className="text-[10px]">{f}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {(!plans || plans.length === 0) && <p className="text-sm text-muted-foreground text-center py-4">No plans defined. Create one above to start onboarding tenants.</p>}
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* MASTER ADMINS                                                   */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="masters" className="space-y-3 mt-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Users with cross-tenant super_admin authority. New master admins are created in the Platform Operations org.</p>
            <CreateMasterDialog onCreate={(d) => createMasterMutation.mutate(d)} pending={createMasterMutation.isPending} />
          </div>
          {(masterAdmins ?? []).map((m) => {
            const isSelf = sessionUser?.id === m.id;
            const isInactive = m.active === "false";
            return (
              <Card key={m.id} data-testid={`card-master-${m.id}`} className={isSelf ? "ring-1 ring-primary" : ""}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center text-white text-sm font-medium">
                      <Crown className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2" data-testid={`text-master-name-${m.id}`}>
                        {m.name}
                        {isSelf && <Badge variant="outline" className="text-[10px]" data-testid={`badge-master-self-${m.id}`}>you</Badge>}
                        {isInactive && <Badge variant="secondary" className="text-[10px]">inactive</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isSelf && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleMasterActiveMutation.mutate({ id: m.id, active: isInactive })}
                          disabled={toggleMasterActiveMutation.isPending}
                          data-testid={`button-toggle-master-${m.id}`}
                        >
                          <Power className="w-3 h-3 mr-1" />{isInactive ? "Reactivate" : "Deactivate"}
                        </Button>
                        <ConfirmDialog
                          title={`Remove master admin "${m.name}"?`}
                          description="This will deactivate/remove this Master Admin access. Account history will be retained."
                          confirmLabel="Remove admin"
                          confirmTestId={`button-confirm-delete-master-${m.id}`}
                          onConfirm={() => deleteMasterMutation.mutate(m.id)}
                          trigger={
                            <IconButton
                              variant="ghost"
                              label={`Remove master admin ${m.name}`}
                              className="h-8 w-8 text-destructive"
                              data-testid={`button-delete-master-${m.id}`}
                            >
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </IconButton>
                          }
                        />
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {(!masterAdmins || masterAdmins.length === 0) && <p className="text-sm text-muted-foreground text-center py-4">No master admins (run scripts/seed-master-admin.ts).</p>}
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* BILLING                                                         */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="billing" className="space-y-4 mt-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Monthly recurring revenue (estimate)</p>
              <p className="text-2xl font-bold mt-1" data-testid="text-billing-mrr">{formatUsd(analytics?.summary.monthlyRecurringRevenueCents ?? 0)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Sum of plan prices across active tenants. Not a Stripe reconciliation.</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Active tenants</p>
              <p className="text-2xl font-bold mt-1" data-testid="text-billing-tenants">{billingTenants?.length ?? analytics?.summary.activeTenantCount ?? 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Messages used (this period)</p>
              <p className="text-2xl font-bold mt-1" data-testid="text-billing-messages">{(analytics?.summary.totalMessagesUsed ?? 0).toLocaleString()}</p>
            </CardContent></Card>
          </div>

          {/* Pending Bills card */}
          {((pendingBills?.length ?? 0) > 0 || pendingBillsLoading) && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm text-amber-800 dark:text-amber-300">Pending Bills</CardTitle>
                  {!pendingBillsLoading && (
                    <span className="text-[11px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5 font-medium">
                      {pendingBills!.length} tenant{pendingBills!.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Tenants on paid plans whose subscription is not active — billing period has passed without payment.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {pendingBillsLoading ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">Loading pending bills…</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" role="table" aria-label="Pending bills">
                      <thead>
                        <tr className="border-b bg-amber-50/60 dark:bg-amber-900/20 text-left text-xs font-medium text-muted-foreground">
                          <th className="px-4 py-2.5 font-medium">Tenant</th>
                          <th className="px-4 py-2.5 font-medium">Plan</th>
                          <th className="px-4 py-2.5 font-medium">Billing period</th>
                          <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                          <th className="px-4 py-2.5 font-medium text-center">Due date</th>
                          <th className="px-4 py-2.5 font-medium text-center">Status</th>
                          <th className="px-4 py-2.5 font-medium text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pendingBills ?? []).map((bill, i) => (
                          <tr
                            key={bill.id}
                            className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? "" : "bg-muted/10"}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold select-none ${tenantAvatarColor(bill.tenantName)}`}>
                                  {tenantInitials(bill.tenantName)}
                                </div>
                                <div>
                                  <p className="font-semibold text-sm leading-snug">{bill.tenantName}</p>
                                  <span className="text-[10px] text-muted-foreground/70 font-mono">/{bill.tenantSlug}</span>
                                  {bill.adminEmail && (
                                    <p className="text-[10px] text-muted-foreground truncate max-w-[160px]">{bill.adminEmail}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs font-medium">{bill.plan}</td>
                            <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{bill.billingPeriod}</td>
                            <td className="px-4 py-3 text-xs font-mono text-right">{formatUsd(bill.planPrice)}/mo</td>
                            <td className="px-4 py-3 text-xs text-center whitespace-nowrap">
                              {new Date(bill.dueDate).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-700 rounded px-1.5 py-0.5 font-medium capitalize">
                                {bill.status}
                              </span>
                            </td>
                            {/* Three actions: Reminder | Payment Link | Manage/Subscribe */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 justify-center flex-wrap">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost" size="sm"
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                      disabled={sendReminderMutation.isPending && reminderPending === bill.id}
                                      onClick={() => sendReminderMutation.mutate(bill.id)}
                                      data-testid={`button-pending-reminder-${bill.id}`}
                                    >
                                      <Mail className="w-3.5 h-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    {sendReminderMutation.isPending && reminderPending === bill.id ? "Sending…" : "Send reminder"}
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost" size="sm"
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                      disabled={sendPaymentLinkMutation.isPending && paymentLinkPending === bill.id}
                                      onClick={() => sendPaymentLinkMutation.mutate(bill.id)}
                                      data-testid={`button-pending-paylink-${bill.id}`}
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    {sendPaymentLinkMutation.isPending && paymentLinkPending === bill.id
                                      ? "Sending…" : "Send payment link to org admin"}
                                  </TooltipContent>
                                </Tooltip>

                                {bill.hasStripeCustomer && bill.hasStripeSubscription ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline" size="sm"
                                        className="h-7 text-xs px-2"
                                        disabled={billingPortalMutation.isPending}
                                        onClick={() => billingPortalMutation.mutate(bill.id)}
                                        data-testid={`button-pending-portal-${bill.id}`}
                                      >
                                        <CreditCard className="w-3 h-3 mr-1" />Manage
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Open Stripe Customer Portal for this tenant</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline" size="sm"
                                        className="h-7 text-xs px-2"
                                        disabled={subscribeMutation.isPending}
                                        onClick={() => subscribeMutation.mutate({
                                        orgId: bill.id,
                                        planId: bill.planId,
                                        successUrl: `${window.location.origin}/admin?subscribed=1`,
                                        cancelUrl: `${window.location.origin}/admin`,
                                      })}
                                        data-testid={`button-pending-subscribe-${bill.id}`}
                                      >
                                        <Sparkles className="w-3 h-3 mr-1" />Subscribe
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Open Stripe Checkout to set up a subscription</TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Billing table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-sm">Tenant billing management</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tenants..."
                    value={billingSearch}
                    onChange={(e) => setBillingSearch(e.target.value)}
                    className="pl-9 h-8 text-sm w-56"
                    aria-label="Search billing tenants"
                    data-testid="input-search-billing"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {billingLoading ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Loading billing data…</p>
              ) : (billingTenants?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No tenants found.</p>
              ) : (() => {
                const q = billingSearch.toLowerCase();
                const rows = (billingTenants ?? []).filter((t) => {
                  // Billing Management only shows tenants with a confirmed Stripe
                  // subscription (StripeSubscriptionId exists). Tenants that have
                  // started checkout but whose webhook hasn't fired yet (hasStripe=false,
                  // status="pending") stay in Pending Billing until activation completes.
                  if (!t.hasStripe) return false;
                  return (
                    !q ||
                    t.name.toLowerCase().includes(q) ||
                    (t.orgAdminEmail ?? "").toLowerCase().includes(q) ||
                    (t.planName ?? "").toLowerCase().includes(q) ||
                    t.subscriptionStatus.toLowerCase().includes(q)
                  );
                });
                const statusBadge = (s: string) => {
                  const lc = s.toLowerCase();
                  if (lc === "active")          return <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>;
                  if (lc === "expired")         return <Badge variant="destructive" className="text-[10px]">Expired</Badge>;
                  if (lc === "cancelled")       return <Badge variant="secondary" className="text-[10px]">Cancelled</Badge>;
                  if (lc === "pending")         return <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">Pending</Badge>;
                  if (lc === "no_subscription") return <Badge variant="outline" className="text-[10px] text-muted-foreground">No Subscription</Badge>;
                  return <Badge variant="outline" className="text-[10px] capitalize">{s}</Badge>;
                };
                return rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No results match your search.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" role="table" aria-label="Billing tenants">
                      <thead>
                        <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                          <th className="px-4 py-2.5 font-medium">Tenant</th>
                          <th className="px-4 py-2.5 font-medium">Admin email</th>
                          <th className="px-4 py-2.5 font-medium">Plan</th>
                          <th className="px-4 py-2.5 font-medium">Subscription since</th>
                          <th className="px-4 py-2.5 font-medium text-center">Expires on</th>
                          <th className="px-4 py-2.5 font-medium text-center">Status</th>
                          <th className="px-4 py-2.5 font-medium text-center">Messages</th>
                          <th className="px-4 py-2.5 font-medium text-center">Users</th>
                          <th className="px-4 py-2.5 font-medium text-center">Bots</th>
                          <th className="px-4 py-2.5 font-medium text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((t, i) => {
                          const isReminding = reminderPending === t.id || (sendReminderMutation.isPending && reminderPending === t.id);
                          return (
                            <tr
                              key={t.id}
                              className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? "" : "bg-muted/10"}`}
                              data-testid={`row-billing-${t.id}`}
                            >
                              {/* Tenant name + slug — matches Active Tenants table style */}
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold select-none ${tenantAvatarColor(t.name)}`}>
                                    {tenantInitials(t.name)}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-sm leading-snug truncate">{t.name}</p>
                                    <span className="text-[10px] text-muted-foreground/70 font-mono">/{t.slug}</span>
                                    {t.isSuspended && (
                                      <Badge variant="destructive" className="text-[9px] px-1 py-0 ml-1">Suspended</Badge>
                                    )}
                                  </div>
                                </div>
                              </td>
                              {/* Admin email */}
                              <td className="px-4 py-3 text-xs text-muted-foreground">
                                {t.orgAdminEmail ? (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3 shrink-0" />
                                    <span className="truncate max-w-[160px]">{t.orgAdminEmail}</span>
                                  </span>
                                ) : (
                                  <span className="italic text-muted-foreground/60">No admin</span>
                                )}
                              </td>
                              {/* Plan */}
                              <td className="px-4 py-3">
                                <p className="font-medium text-xs capitalize">{t.planName}</p>
                                <p className="text-[11px] text-muted-foreground">{formatUsd(t.planPrice)}/mo</p>
                              </td>
                              {/* Subscription since */}
                              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                                {t.subscriptionStartedAt
                                  ? new Date(t.subscriptionStartedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                                  : <span className="opacity-50">—</span>}
                              </td>
                              {/* Expires on — Stripe CurrentPeriodEnd; "—" for non-Stripe orgs */}
                              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap text-center">
                                {t.subscriptionExpiresAt
                                  ? new Date(t.subscriptionExpiresAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                                  : <span className="opacity-50">—</span>}
                              </td>
                              {/* Status */}
                              <td className="px-4 py-3 text-center">
                                {statusBadge(t.subscriptionStatus)}
                              </td>
                              {/* Messages used — actual usage only, no limit shown */}
                              <td className="px-4 py-3 text-center">
                                <p className="text-xs tabular-nums">{t.messagesUsed.toLocaleString()}</p>
                              </td>
                              {/* Users */}
                              <td className="px-4 py-3 text-center text-xs tabular-nums font-medium">{t.userCount}</td>
                              {/* Bots */}
                              <td className="px-4 py-3 text-center text-xs tabular-nums font-medium">{t.botCount}</td>
                              {/* Actions */}
                              <td className="px-4 py-3 text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs px-2.5"
                                  disabled={isReminding || !t.orgAdminEmail}
                                  onClick={() => sendReminderMutation.mutate(t.id)}
                                  title={t.orgAdminEmail ? `Send reminder to ${t.orgAdminEmail}` : "No admin email found"}
                                  data-testid={`button-billing-reminder-${t.id}`}
                                >
                                  <Mail className="w-3 h-3 mr-1" />
                                  {isReminding ? "Sending…" : "Send reminder"}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* REPORTS                                                         */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="reports" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-sm">Downloadable reports</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-4 p-3 border rounded-md">
                <div>
                  <p className="text-sm font-medium">Tenant report (CSV)</p>
                  <p className="text-xs text-muted-foreground">Tenant name, slug, admin email, plan, price, subscription start &amp; expiry, status, messages used, users, bots, conversations.</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTenantCsv}
                  disabled={tenantCsvPending}
                  data-testid="button-download-tenant-report"
                >
                  <Download className="w-4 h-4 mr-1" />
                  {tenantCsvPending ? "Downloading…" : "Download"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* USERS (preserved from old panel — cross-tenant user mgmt)        */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="users" className="space-y-3 mt-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search users..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9" aria-label="Search users" data-testid="input-search-users" />
            </div>
            <CreateTenantUserDialog
              organizations={(stats?.organizations ?? []).filter((o: any) => o.slug !== "platform")}
              onCreateTenantUser={(d) => createTenantUserMutation.mutate(d)}
              onCreateMasterAdmin={(d) => createMasterMutation.mutate(d)}
              pending={createTenantUserMutation.isPending || createMasterMutation.isPending}
            />
          </div>
          {filteredUsers.map((u: any) => (
            <Card key={u.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                    {u.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium" data-testid={`text-user-name-${u.id}`}>{u.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5" data-testid={`text-user-tenant-${u.id}`}>
                      <Building2 className="w-3 h-3" />
                      {u.organizationName || orgNameById[u.organizationId] || "Not Assigned"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={u.role} onValueChange={(val) => updateUserRoleMutation.mutate({ id: u.id, role: val })}>
                    <SelectTrigger className="w-[140px] h-8 text-xs" aria-label={`Role for ${u.name}`} data-testid={`select-role-${u.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Member</SelectItem>
                      <SelectItem value="admin">OrgAdmin</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="liveagent">Live Agent</SelectItem>
                      <SelectItem value="auditor">Auditor</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  {u.role === "super_admin" ? (
                    <Badge className="bg-amber-600 text-white text-[10px]"><Crown className="w-3 h-3 mr-0.5" />Super</Badge>
                  ) : u.role === "admin" ? (
                    <Badge variant="default" className="text-[10px]"><Shield className="w-3 h-3 mr-0.5" />OrgAdmin</Badge>
                  ) : u.role === "supervisor" ? (
                    <Badge variant="secondary" className="text-[10px]">Supervisor</Badge>
                  ) : u.role === "liveagent" ? (
                    <Badge variant="secondary" className="text-[10px]">Live Agent</Badge>
                  ) : u.role === "auditor" ? (
                    <Badge variant="secondary" className="text-[10px]">Auditor</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">Member</Badge>
                  )}
                  {u.role !== "super_admin" && (
                    <ConfirmDialog
                      title={`Deactivate user "${u.name}"?`}
                      description={`This deactivates the account "${u.name}". They're signed out immediately and their sessions revoked, so they lose access right away. Their account record and history are retained.`}
                      confirmLabel="Deactivate user"
                      confirmTestId={`button-confirm-delete-user-${u.id}`}
                      onConfirm={() => deleteUserMutation.mutate(u.id)}
                      trigger={
                        <IconButton variant="ghost" label={`Deactivate user ${u.name}`} className="h-8 w-8 text-destructive" data-testid={`button-delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </IconButton>
                      }
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredUsers.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No users found</p>}
        </TabsContent>

        {/* ─────────────────────────────────────────────────────────────── */}
        {/* CONVERSATIONS (preserved from old panel)                         */}
        {/* ─────────────────────────────────────────────────────────────── */}
        <TabsContent value="conversations" className="space-y-3 mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by session, bot, channel, user..." value={convoSearch} onChange={(e) => setConvoSearch(e.target.value)} className="pl-9" aria-label="Search conversations" data-testid="input-search-convos" />
            </div>
            <Select value={convoOrgFilter} onValueChange={setConvoOrgFilter}>
              <SelectTrigger className="w-[200px] h-9 text-xs" aria-label="Filter by tenant" data-testid="select-filter-convo-org"><SelectValue placeholder="All tenants" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All tenants</SelectItem>
                {(stats?.organizations ?? []).filter((o: any) => o.slug !== "platform").map((o: any) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground ml-auto" data-testid="text-convo-count">
              {filteredConversations.length} of {conversations?.length ?? 0}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2 max-h-[500px] overflow-auto">
              {filteredConversations.map((c: any) => (
                <Card key={c.id} className={`cursor-pointer transition-colors ${selectedConvo === c.id ? "ring-2 ring-primary" : ""}`} onClick={() => setSelectedConvo(c.id)} data-testid={`card-convo-${c.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium truncate">{c.chatbot?.name || "Unknown Bot"}</p>
                      <Badge variant="outline" className="text-[10px]">{c.sessionId?.substring(0, 12)}...</Badge>
                    </div>
                    {(c.userName || c.userEmail) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {c.userName && <span>{c.userName}</span>}
                        {c.userEmail && <span> ({c.userEmail})</span>}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">{c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}</p>
                  </CardContent>
                </Card>
              ))}
              {filteredConversations.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No conversations match the current filter.</p>
              )}
            </div>
            <Card>
              <CardHeader><CardTitle className="text-sm">Messages</CardTitle></CardHeader>
              <CardContent className="max-h-[400px] overflow-auto space-y-2">
                {selectedConvo ? (
                  convoMessages?.map((m: any) => (
                    <div key={m.id} className={`p-2 rounded-md text-sm ${m.role === "user" ? "bg-primary/10 ml-8" : "bg-muted mr-8"}`}>
                      <p className="text-[10px] font-medium text-muted-foreground mb-1">{m.role}</p>
                      <p>{m.content}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">Select a conversation</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Impersonation confirmation dialog ─────────────────────────── */}
      <Dialog
        open={!!impersonateTarget}
        onOpenChange={(open) => { if (!open) setImpersonateTarget(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              Impersonate Tenant
            </DialogTitle>
            <DialogDescription>
              You are about to open a tenant session as the OrgAdmin of this
              organization. This action is audited.
            </DialogDescription>
          </DialogHeader>

          {impersonateTarget && (
            <div className="rounded-md border bg-muted/40 px-4 py-3 space-y-1 text-sm">
              <p><span className="text-muted-foreground">Organization:</span>{" "}
                <span className="font-semibold">{impersonateTarget.name}</span>
              </p>
              <p><span className="text-muted-foreground">Slug:</span>{" "}
                <span className="font-mono text-xs">/{impersonateTarget.slug}</span>
              </p>
              <p><span className="text-muted-foreground">Plan:</span>{" "}
                <span className="capitalize">{impersonateTarget.plan}</span>
              </p>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            A new browser tab will open as the tenant OrgAdmin. Your Platform
            Admin session in this tab is unaffected. The audit log will record
            this impersonation with your admin ID and IP.
          </p>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setImpersonateTarget(null)}
              disabled={impersonateMutation.isPending}
              data-testid="button-cancel-impersonate"
            >
              Cancel
            </Button>
            <Button
              onClick={() => impersonateTarget && impersonateMutation.mutate(impersonateTarget.id)}
              disabled={impersonateMutation.isPending}
              data-testid="button-confirm-impersonate"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {impersonateMutation.isPending ? "Opening session…" : "Impersonate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: CreateTenantDialog
// ────────────────────────────────────────────────────────────────────────────
function CreateTenantDialog({ plans, onCreate, pending }: { plans: Plan[]; onCreate: (d: { name: string; slug: string; plan?: string; adminEmail?: string; adminPassword?: string; adminName?: string }) => void; pending: boolean }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [plan, setPlan] = useState<string>("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminName, setAdminName] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="button-new-tenant"><Plus className="w-4 h-4 mr-1" />New tenant</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new tenant</DialogTitle>
          <DialogDescription>Provisions a new organization. The slug is the URL identifier and must be unique.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="new-tenant-name">Name</Label>
            <Input id="new-tenant-name" value={name} onChange={(e) => {
              setName(e.target.value);
              if (!slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, ""));
            }} data-testid="input-new-tenant-name" />
          </div>
          <div>
            <Label htmlFor="new-tenant-slug">Slug</Label>
            <Input id="new-tenant-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="acme-co" data-testid="input-new-tenant-slug" />
          </div>
          <div>
            <Label htmlFor="new-tenant-plan">Plan (optional)</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger id="new-tenant-plan" data-testid="select-new-tenant-plan"><SelectValue placeholder="Default starter" /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.id} value={p.slug}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="border-t pt-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground">Organization admin (optional — lets the tenant log in right away)</p>
            <div>
              <Label htmlFor="new-tenant-admin-email">Admin email</Label>
              <Input id="new-tenant-admin-email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@acme.com" data-testid="input-new-tenant-admin-email" />
            </div>
            {adminEmail && (
              <>
                <div>
                  <Label htmlFor="new-tenant-admin-password">Admin password</Label>
                  <Input id="new-tenant-admin-password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} data-testid="input-new-tenant-admin-password" />
                </div>
                <div>
                  <Label htmlFor="new-tenant-admin-name">Admin name (optional)</Label>
                  <Input id="new-tenant-admin-name" value={adminName} onChange={(e) => setAdminName(e.target.value)} data-testid="input-new-tenant-admin-name" />
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-cancel-new-tenant">Cancel</Button>
          <Button
            disabled={!name || !slug || pending || (!!adminEmail && !adminPassword)}
            onClick={() => {
              onCreate({
                name, slug, plan: plan || undefined,
                adminEmail: adminEmail || undefined,
                adminPassword: adminEmail ? adminPassword : undefined,
                adminName: adminEmail && adminName ? adminName : undefined,
              });
              setOpen(false);
              setName(""); setSlug(""); setPlan(""); setAdminEmail(""); setAdminPassword(""); setAdminName("");
            }}
            data-testid="button-submit-new-tenant"
          >Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: PlanDialog (handles both create and edit)
// ────────────────────────────────────────────────────────────────────────────
function PlanDialog({
  mode, initial, onSubmit, pending, triggerTestId, triggerLabel,
}: {
  mode: "create" | "edit";
  initial?: Plan;
  onSubmit: (d: any) => void;
  pending: boolean;
  triggerTestId: string;
  triggerLabel: string;
}) {
  const [open, setOpen] = useState(false);
  // D5 fix: explicitly return focus to the opening control when the dialog
  // closes (Escape, Cancel, X, or successful save). Radix's automatic
  // focus-return proved unreliable here; onCloseAutoFocus below makes it
  // deterministic, with the plans tab as a fallback if the trigger is gone.
  const triggerBtnRef = React.useRef<HTMLButtonElement>(null);
  // Deterministic Escape: capture-phase document listener survives
  // third-party overlays (like the since-removed accessiBe) stopping propagation of Escape before
  // it reaches Radix's own handlers.
  React.useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      // Let a nested Radix overlay (Select/Popover/menu) consume Escape first.
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onEsc, true);
    return () => document.removeEventListener("keydown", onEsc, true);
  }, [open]);
  const returnFocus = (e: Event) => {
    e.preventDefault();
    const el = triggerBtnRef.current;
    if (el && document.contains(el)) el.focus();
    else document.querySelector<HTMLElement>('[data-testid="button-new-plan"], [data-testid="tab-plans"]')?.focus();
  };
  const [name, setName] = useState(initial?.name ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [price, setPrice] = useState(String(initial?.price ?? 0));
  const [botLimit, setBotLimit] = useState(String(initial?.botLimit ?? 1));
  // agentLimit and monthlyMessages are no longer configurable in the plan UI
  // (seat and message limits have been removed from subscription plans).
  // Preserve existing DB values through submit so editing a plan never zeros them.
  const [agentLimit] = useState(String(initial?.agentLimit ?? 0));
  const [monthlyMessages] = useState(String(initial?.monthlyMessages ?? 0));
  const [features, setFeatures] = useState<string[]>(initial?.features ?? []);
  const [featureSearch, setFeatureSearch] = useState("");
  // Master-defined feature catalog. Plans are composed by ticking items
  // from this list, not by free-typing strings — any custom string would
  // never be recognised by PlanFeatureService.HasFeature on the server.
  const { data: featureCatalog } = useQuery<{ key: string; label: string; group: string }[]>({
    queryKey: ["/api/admin/plans/features"],
  });
  const [billingInterval, setBillingInterval] = useState<"month" | "year">(
    (initial?.billingInterval as "month" | "year") ?? "month",
  );
  const [stripePriceId, setStripePriceId] = useState(initial?.stripePriceId ?? "");
  const [stripeProductId, setStripeProductId] = useState(initial?.stripeProductId ?? "");
  const stripePriceValid = !stripePriceId.trim() || /^price_[A-Za-z0-9_]+$/.test(stripePriceId.trim());
  const stripeProductValid = !stripeProductId.trim() || /^prod_[A-Za-z0-9_]+$/.test(stripeProductId.trim());

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button ref={triggerBtnRef} variant={mode === "create" ? "default" : "outline"} size="sm" data-testid={triggerTestId}>
          {mode === "create" ? <><Plus className="w-4 h-4 mr-1" />{triggerLabel}</> : triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[90vh] flex flex-col overflow-hidden"
        onCloseAutoFocus={returnFocus}
        // Deterministic Escape: third-party overlays (e.g. the since-removed accessiBe
        // widget) can swallow the document-level keydown Radix relies on, so
        // handle Escape on the content itself for single-press dismissal.
        onKeyDown={(e) => {
          if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
          if (e.key === "Escape") {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{mode === "create" ? "Create plan" : `Edit ${initial?.name}`}</DialogTitle>
          <DialogDescription>
            Saving auto-syncs to Stripe (creates Product + Price) when Stripe is connected. Features below come from the master capability catalog and gate which modules tenants on this plan can access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="plan-name">Name</Label>
              <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-plan-name" />
            </div>
            <div>
              <Label htmlFor="plan-slug">Slug</Label>
              <Input id="plan-slug" value={slug} onChange={(e) => setSlug(e.target.value)} disabled={mode === "edit"} data-testid="input-plan-slug" />
            </div>
            <div>
              <Label htmlFor="plan-price">Price (USD)</Label>
              <Input id="plan-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="input-plan-price" />
            </div>
            <div>
              <Label htmlFor="plan-bot-limit">Bot limit</Label>
              <Input id="plan-bot-limit" type="number" value={botLimit} onChange={(e) => setBotLimit(e.target.value)} data-testid="input-plan-bot-limit" />
            </div>
            <div>
              <Label htmlFor="plan-interval">Billing interval</Label>
              <Select value={billingInterval} onValueChange={(v) => setBillingInterval(v as "month" | "year")}>
                <SelectTrigger id="plan-interval" data-testid="select-plan-interval"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              Stripe — paste the Price (and optionally Product) IDs from your Stripe dashboard so checkout can use this plan. Leave blank to disable Stripe for it.
            </p>
            <div>
              <Label htmlFor="plan-stripe-price">Stripe Price ID</Label>
              <Input
                id="plan-stripe-price"
                value={stripePriceId}
                onChange={(e) => setStripePriceId(e.target.value)}
                placeholder="price_1ABCxyz..."
                data-testid="input-plan-stripe-price"
              />
              {!stripePriceValid && (
                <p className="text-xs text-destructive mt-1">Must start with "price_"</p>
              )}
            </div>
            <div>
              <Label htmlFor="plan-stripe-product">Stripe Product ID (optional)</Label>
              <Input
                id="plan-stripe-product"
                value={stripeProductId}
                onChange={(e) => setStripeProductId(e.target.value)}
                placeholder="prod_ABCxyz..."
                data-testid="input-plan-stripe-product"
              />
              {!stripeProductValid && (
                <p className="text-xs text-destructive mt-1">Must start with "prod_"</p>
              )}
            </div>
          </div>
          <div className="pt-2 border-t">
            <Label>Features included in this plan</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Tick the capabilities this plan should grant. Tenants on the plan can use only the modules checked here.
            </p>
            {!featureCatalog && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[0,1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            )}
            {featureCatalog && (
              <Input
                value={featureSearch}
                onChange={(e) => setFeatureSearch(e.target.value)}
                placeholder="Search features by name or key…"
                className="mt-2 h-8 text-xs"
                data-testid="input-feature-search"
              />
            )}
            {featureCatalog && (() => {
              const q = featureSearch.trim().toLowerCase();
              const visible = q
                ? featureCatalog.filter(f => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q))
                : featureCatalog;
              const groups = visible.reduce<Record<string, typeof featureCatalog>>((acc, f) => {
                (acc[f.group] ||= []).push(f); return acc;
              }, {});
              const knownOrder = ["Main", "Tools", "Calls", "Tickets", "Live Chat", "Integrations", "Organization"];
              // Render every group the catalog returns, known ones first so
              // a new backend group can never be silently dropped.
              // "Compliance Frameworks" is rendered inline under the compliance
              // parent — never as a standalone group in the main grid.
              // "Compliance Frameworks" are no longer plan-controlled; exclude them from the grid.
              const groupOrder = [...knownOrder, ...Object.keys(groups).filter(g => !knownOrder.includes(g) && g !== "Compliance Frameworks")];
              const sel = new Set(features);
              // Parent module → its child capability keys. Unchecking a
              // parent removes the children too; checking a child pulls
              // the parent in so the module itself stays reachable.
              const childrenOf = (parent: string) =>
                featureCatalog.filter(c => c.key.startsWith(parent + ".")).map(c => c.key);
              const parentOf = (key: string) => {
                const dot = key.indexOf(".");
                if (dot < 0) return null;
                const p = key.slice(0, dot);
                return featureCatalog.some(c => c.key === p) ? p : null;
              };
              const toggle = (key: string, checked: boolean) => {
                const next = new Set(features);
                if (checked) {
                  next.add(key);
                  const p = parentOf(key);
                  if (p) next.add(p);
                  // Selecting a parent module selects its children too
                  for (const c of childrenOf(key)) next.add(c);
                } else {
                  next.delete(key);
                  for (const c of childrenOf(key)) next.delete(c);
                }
                setFeatures([...next]);
              };
              return (
                <div className="mt-3 space-y-3" data-testid="features-grid">
                  {visible.length === 0 && (
                    <p className="text-xs text-muted-foreground">No features match "{featureSearch}".</p>
                  )}
                  {groupOrder.filter(g => groups[g]).map(g => (
                    <div key={g}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{g}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {groups[g].map(f => {
                          const checked = sel.has(f.key);
                          // Compliance framework selection is no longer part of plan configuration.
                          // The "compliance" module feature still exists as a standard plan capability
                          // (it gates Compliance Center access), but framework sub-keys are not
                          // plan-controlled and the expandable framework panel has been removed.
                          return (
                            <label
                              key={f.key}
                              className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 cursor-pointer hover-elevate ${checked ? "border-primary bg-primary/5" : "border-border"}`}
                              data-testid={`feature-toggle-${f.key}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggle(f.key, e.target.checked)}
                                className="mt-0.5"
                                aria-label={f.label}
                                data-testid={`checkbox-feature-${f.key}`}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium leading-tight">{f.label}</div>
                                <div className="text-[10px] text-muted-foreground font-mono truncate">{f.key}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {features.some(f => !(featureCatalog ?? []).find(c => c.key === f)) && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                <span className="font-semibold">Legacy keys retained:</span>{" "}
                {features.filter(f => !(featureCatalog ?? []).find(c => c.key === f)).join(", ")}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-cancel-plan">Cancel</Button>
          <Button
            disabled={!name || !slug || pending || !stripePriceValid || !stripeProductValid}
            onClick={() => {
              onSubmit({
                name, slug,
                price: Number(price),
                botLimit: Number(botLimit),
                agentLimit: Number(agentLimit),
                monthlyMessages: Number(monthlyMessages),
                features,
                billingInterval,
                stripePriceId: stripePriceId.trim(),
                stripeProductId: stripeProductId.trim(),
              });
              setOpen(false);
            }}
            data-testid="button-submit-plan"
          >{mode === "create" ? "Create" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: CreateMasterDialog
// ────────────────────────────────────────────────────────────────────────────
function CreateMasterDialog({ onCreate, pending }: { onCreate: (d: { email: string; name: string; password: string }) => void; pending: boolean }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const valid = email && name && password.length >= 8;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="button-new-master"><Plus className="w-4 h-4 mr-1" />Add master admin</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add master admin</DialogTitle>
          <DialogDescription>This creates a new super_admin in the Platform Operations org. They get full cross-tenant access — be careful.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="new-master-name">Name</Label>
            <Input id="new-master-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-new-master-name" />
          </div>
          <div>
            <Label htmlFor="new-master-email">Email</Label>
            <Input id="new-master-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-new-master-email" />
          </div>
          <div>
            <Label htmlFor="new-master-password">Password (min 8 chars)</Label>
            <Input id="new-master-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-new-master-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-cancel-new-master">Cancel</Button>
          <Button
            disabled={!valid || pending}
            onClick={() => {
              onCreate({ email, name, password });
              setOpen(false);
              setEmail(""); setName(""); setPassword("");
            }}
            data-testid="button-submit-new-master"
          >Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: CreateTenantUserDialog
// SA creates a tenant-scoped user (OrgAdmin, Member, Supervisor, LiveAgent,
// Auditor) or a new Super Admin from one unified dialog.
// ────────────────────────────────────────────────────────────────────────────
const TENANT_SCOPED_ROLES = new Set(["orgadmin", "member", "supervisor", "liveagent", "auditor"]);

function CreateTenantUserDialog({
  organizations,
  onCreateTenantUser,
  onCreateMasterAdmin,
  pending,
}: {
  organizations: Array<{ id: string; name: string; slug: string }>;
  onCreateTenantUser: (d: { email: string; name: string; password: string; role: string; organizationId: string }) => void;
  onCreateMasterAdmin: (d: { email: string; name: string; password: string }) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("member");
  const [orgId, setOrgId] = useState("");
  const [orgError, setOrgError] = useState("");

  const isTenantRole = TENANT_SCOPED_ROLES.has(role);
  const valid = email && name && password.length >= 8 && (!isTenantRole || orgId);

  function handleSubmit() {
    if (isTenantRole && !orgId) {
      setOrgError("Organization is required for this role.");
      return;
    }
    setOrgError("");
    if (role === "super_admin") {
      onCreateMasterAdmin({ email, name, password });
    } else {
      onCreateTenantUser({ email, name, password, role, organizationId: orgId });
    }
    setOpen(false);
    setEmail(""); setName(""); setPassword(""); setRole("member"); setOrgId(""); setOrgError("");
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="button-new-user"><Plus className="w-4 h-4 mr-1" />New User</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription>
            Create a new user. For tenant-scoped roles (OrgAdmin, Member, Supervisor, Live Agent, Auditor) an organization must be selected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="nu-name">Full name</Label>
            <Input id="nu-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-new-user-name" />
          </div>
          <div>
            <Label htmlFor="nu-email">Email</Label>
            <Input id="nu-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-new-user-email" />
          </div>
          <div>
            <Label htmlFor="nu-password">Password <span className="text-muted-foreground font-normal">(min 8 chars)</span></Label>
            <Input id="nu-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-new-user-password" />
          </div>
          <div>
            <Label htmlFor="nu-role">Role</Label>
            <Select value={role} onValueChange={(v) => { setRole(v); setOrgId(""); setOrgError(""); }}>
              <SelectTrigger id="nu-role" data-testid="select-new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="orgadmin">OrgAdmin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="liveagent">Live Agent</SelectItem>
                <SelectItem value="auditor">Auditor</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isTenantRole && (
            <div>
              <Label htmlFor="nu-org">Organization <span className="text-destructive">*</span></Label>
              <Select value={orgId} onValueChange={(v) => { setOrgId(v); setOrgError(""); }}>
                <SelectTrigger id="nu-org" data-testid="select-new-user-org" className={orgError ? "border-destructive" : ""}>
                  <SelectValue placeholder="Select an organization…" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name} <span className="text-muted-foreground text-xs ml-1">({o.slug})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {orgError && <p className="text-xs text-destructive mt-1">{orgError}</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-cancel-new-user">Cancel</Button>
          <Button
            disabled={!valid || pending}
            onClick={handleSubmit}
            data-testid="button-submit-new-user"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────
// TenantComplianceSettings
// Collapsible "Compliance Settings" section shown inside each tenant card on the
// Organizations tab. Shows frameworks the Super Admin has disabled for this
// specific tenant and lets them restore access via the shared enable dialog.
// ────────────────────────────────────────────────────────────────────────────
type ComplianceFrameworkOverride = {
  key: string; name: string;
  disabledByAdmin: boolean;
  disabledAt?: string; disabledByEmail?: string; disabledByName?: string;
};
type TenantOverrideData = {
  organizationId: string;
  frameworks: ComplianceFrameworkOverride[];
};

function TenantComplianceSettings({ org }: { org: OrgRow }) {
  const [open, setOpen] = useState(false);
  const [enableTarget, setEnableTarget] = useState<{
    key: string; name: string;
  } | null>(null);

  const { data: overrideData, isLoading } = useQuery<TenantOverrideData>({
    queryKey: [`/api/admin/organizations/${org.id}/compliance-overrides`],
    enabled: open,
    staleTime: 0,
  });

  const disabledFrameworks = (overrideData?.frameworks ?? []).filter(
    (f) => f.disabledByAdmin,
  );

  return (
    <>
      {/* Collapsible header row */}
      <div className="border-t mt-3 pt-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
          aria-expanded={open}
          data-testid={`button-compliance-settings-toggle-${org.id}`}
        >
          {open ? (
            <ChevronDown className="w-3 h-3 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="w-3 h-3 shrink-0" aria-hidden="true" />
          )}
          <Shield className="w-3 h-3 shrink-0" aria-hidden="true" />
          Compliance Settings
          {!open && disabledFrameworks.length === 0 && overrideData && (
            <span className="ml-1 text-[10px] text-muted-foreground/60">(no overrides)</span>
          )}
          {!open && disabledFrameworks.length > 0 && (
            <span className="ml-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
              {disabledFrameworks.length} disabled
            </span>
          )}
        </button>

        {open && (
          <div className="mt-3 space-y-2" data-testid={`section-compliance-settings-${org.id}`}>
            {/* Section header */}
            <div className="rounded-md border border-amber-500/25 bg-amber-500/5 px-4 py-3 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                Compliance Framework Overrides
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                <span>Tenant: <strong className="text-foreground">{org.name}</strong></span>
              </div>
            </div>

            {/* Body */}
            {isLoading ? (
              <div className="space-y-2">
                {[0, 1].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : disabledFrameworks.length === 0 ? (
              <p className="text-xs text-muted-foreground px-1 py-2" data-testid={`text-no-overrides-${org.id}`}>
                No framework overrides active — this tenant has full plan access.
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground px-1">Disabled Frameworks:</p>
                <div className="space-y-2">
                  {disabledFrameworks.map((fw) => (
                    <div
                      key={fw.key}
                      className="flex items-center justify-between gap-3 rounded-md border border-amber-500/20 bg-background px-3 py-2"
                      data-testid={`compliance-override-row-${org.id}-${fw.key}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{fw.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {fw.disabledAt && fw.disabledAt !== "unknown"
                            ? `Disabled ${new Date(fw.disabledAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`
                            : "Disabled"}
                          {fw.disabledByEmail && fw.disabledByEmail !== "unknown"
                            ? ` by ${fw.disabledByEmail}`
                            : " by Platform Administrator"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 h-7 text-xs border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                        onClick={() => setEnableTarget({ key: fw.key, name: fw.name })}
                        data-testid={`button-enable-override-${org.id}-${fw.key}`}
                        aria-label={`Enable ${fw.name} for ${org.name}`}
                      >
                        Enable Override
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Enable confirmation dialog */}
      {enableTarget && (
        <ComplianceEnableOverrideDialog
          frameworkKey={enableTarget.key}
          frameworkName={enableTarget.name}
          orgId={org.id}
          orgName={org.name}
          onClose={() => setEnableTarget(null)}
          onEnabled={() => setEnableTarget(null)}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: ComplianceOverrideDialog
// SA can enable/disable HIPAA, FERPA, and WCAG for a specific tenant.
// Toggles save immediately (no batch Save button). On failure the previous
// state is restored. The same DB column (ComplianceFrameworkOverridesJson)
// is the single source of truth for both SA overrides and tenant compliance UI.
// ────────────────────────────────────────────────────────────────────────────

// Only these 3 frameworks are controlled from the SA tenant table.
const SA_COMPLIANCE_FRAMEWORKS: Record<string, string> = {
  hipaa: "Health information privacy and security compliance",
  ferpa: "Education records privacy compliance",
  wcag:  "Web accessibility compliance (WCAG 2.2 AA)",
};

function ComplianceOverrideDialog({ org }: { org: OrgRow }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [savingKey, setSavingKey] = React.useState<string | null>(null);

  const { data: overrideData, isLoading, refetch } = useQuery<{
    organizationId: string;
    frameworks: {
      key: string; name: string; disabledByAdmin: boolean;
      disabledAt?: string; disabledByEmail?: string; disabledByName?: string;
    }[];
  }>({
    queryKey: [`/api/admin/organizations/${org.id}/compliance-overrides`],
    enabled: open,
  });

  // Local disabled set mirrors the server state; toggling is optimistic.
  const [disabled, setDisabled] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (overrideData) {
      setDisabled(new Set(
        overrideData.frameworks.filter(f => f.disabledByAdmin).map(f => f.key)
      ));
    }
  }, [overrideData]);

  // Visible rows: only the 3 SA-controlled frameworks (intersected with what the
  // backend returned so we never fabricate entries).
  const allFwKeys = Object.keys(SA_COMPLIANCE_FRAMEWORKS);
  const backendKeys = new Set((overrideData?.frameworks ?? []).map(f => f.key));
  // Always show all 3; mark keys missing from backend as "not in plan" (grayed).
  const rows = allFwKeys.map(key => ({
    key,
    description: SA_COMPLIANCE_FRAMEWORKS[key],
    name: overrideData?.frameworks.find(f => f.key === key)?.name ?? key.toUpperCase(),
    inPlan: backendKeys.has(key) || backendKeys.size === 0, // show all if backend hasn't loaded yet
    ...(overrideData?.frameworks.find(f => f.key === key) ?? {}),
  }));

  // Toggle a single framework — saves immediately.
  const toggleFramework = async (key: string, currentDisabledSet: Set<string>) => {
    if (savingKey) return; // prevent concurrent saves
    const isCurrentlyOff = currentDisabledSet.has(key);
    const newDisabled = new Set(currentDisabledSet);
    if (isCurrentlyOff) newDisabled.delete(key); else newDisabled.add(key);

    // Optimistic update
    setDisabled(newDisabled);
    setSavingKey(key);
    try {
      const res = await apiRequest("PATCH",
        `/api/admin/organizations/${org.id}/compliance-overrides`,
        { disabledFrameworks: [...newDisabled] });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? `Save failed (${res.status})`);
      }
      // Re-fetch to get server-confirmed state (incl. timestamps).
      await refetch();
      toast({
        title: isCurrentlyOff ? `${key.toUpperCase()} enabled` : `${key.toUpperCase()} disabled`,
        description: isCurrentlyOff
          ? `${key.toUpperCase()} is now available to ${org.name}.`
          : `${key.toUpperCase()} is now hidden from ${org.name}. Org admins notified.`,
      });
    } catch (err: any) {
      // Restore previous state on failure.
      setDisabled(currentDisabledSet);
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          data-testid={`button-compliance-override-${org.id}`}
        >
          <ShieldCheck className="w-3 h-3 mr-1" aria-hidden="true" />Compliance
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Compliance — {org.name}</DialogTitle>
          <DialogDescription>
            Enable or disable compliance frameworks for this tenant. Changes take effect
            immediately and are reflected in their Compliance Center. Org admins are
            notified by email when access changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {isLoading ? (
            [0, 1, 2].map(i => <Skeleton key={i} className="h-[68px] w-full rounded-md" />)
          ) : (
            rows.map(fw => {
              const isOff = disabled.has(fw.key);
              const isSaving = savingKey === fw.key;
              return (
                <div
                  key={fw.key}
                  className={`rounded-lg border px-4 py-3 transition-colors ${isOff ? "border-destructive/30 bg-destructive/5" : "border-border bg-muted/20"}`}
                  data-testid={`compliance-fw-row-${fw.key}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold leading-tight">{fw.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{fw.description}</p>
                      {isOff && (fw as any).disabledAt && (fw as any).disabledAt !== "unknown" && (
                        <p className="text-[10px] text-destructive/70 mt-1">
                          Disabled {new Date((fw as any).disabledAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          {(fw as any).disabledByName && (fw as any).disabledByName !== "unknown" ? ` by ${(fw as any).disabledByName}` : ""}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={!isOff}
                      disabled={isSaving || !!savingKey}
                      onCheckedChange={() => toggleFramework(fw.key, disabled)}
                      aria-label={`${isOff ? "Enable" : "Disable"} ${fw.name} for ${org.name}`}
                      data-testid={`switch-compliance-fw-${fw.key}`}
                    />
                  </div>
                  {isSaving && (
                    <p className="text-[10px] text-muted-foreground mt-1 animate-pulse">Saving…</p>
                  )}
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: EditTenantDialog
// Lets a Super Admin update a tenant's name, tagline, logo, and brand color
// via the existing PATCH /api/admin/organizations/{id} endpoint.
// ────────────────────────────────────────────────────────────────────────────
function EditTenantDialog({
  org,
  onSave,
  pending,
}: {
  org: OrgRow;
  onSave: (id: string, data: Record<string, unknown>) => void;
  pending: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState(org.name);
  const [tagline, setTagline] = React.useState(org.tagline ?? "");
  const [logo, setLogo] = React.useState(org.logo ?? "");
  const [brandColor, setBrandColor] = React.useState(org.brandColor ?? "#0c3547");

  // Sync form when dialog opens so stale state is not shown.
  React.useEffect(() => {
    if (open) {
      setName(org.name);
      setTagline(org.tagline ?? "");
      setLogo(org.logo ?? "");
      setBrandColor(org.brandColor ?? "#0c3547");
    }
  }, [open, org]);

  const save = () => {
    onSave(org.id, {
      name: name.trim() || org.name,
      tagline: tagline || null,
      logo: logo || null,
      brandColor: brandColor || null,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <IconButton
        variant="ghost"
        label={`Edit ${org.name}`}
        className="h-8 w-8"
        title="Edit tenant info"
        onClick={() => setOpen(true)}
        data-testid={`button-edit-org-${org.id}`}
      >
        <Pencil className="w-4 h-4" aria-hidden="true" />
      </IconButton>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit — {org.name}</DialogTitle>
          <DialogDescription>
            Update tenant display fields. Use the plan selector in the card to change plans and limits.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label htmlFor={`edit-name-${org.id}`}>Name</Label>
            <Input
              id={`edit-name-${org.id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid={`input-edit-tenant-name-${org.id}`}
            />
          </div>
          <div>
            <Label htmlFor={`edit-tagline-${org.id}`}>
              Tagline <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id={`edit-tagline-${org.id}`}
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="A short description"
              data-testid={`input-edit-tenant-tagline-${org.id}`}
            />
          </div>
          <div>
            <Label htmlFor={`edit-logo-${org.id}`}>
              Logo URL <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id={`edit-logo-${org.id}`}
              value={logo}
              onChange={(e) => setLogo(e.target.value)}
              placeholder="https://example.com/logo.png"
              data-testid={`input-edit-tenant-logo-${org.id}`}
            />
          </div>
          <div>
            <Label htmlFor={`edit-color-${org.id}`}>Brand Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                id={`edit-color-${org.id}`}
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-input bg-transparent"
                data-testid={`input-edit-tenant-color-${org.id}`}
              />
              <Input
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                placeholder="#0c3547"
                className="w-32 font-mono text-sm"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={pending} data-testid={`button-save-edit-tenant-${org.id}`}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: TenantSmtpDialog
// Super Admin configures a per-tenant SMTP / SendGrid provider without
// needing to impersonate the org admin.
// ────────────────────────────────────────────────────────────────────────────
function TenantSmtpDialog({ org }: { org: OrgRow }) {
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    enabled: false, provider: "smtp", host: "", port: 587,
    user: "", password: "", useSsl: true, fromEmail: "", fromName: "", sendGridApiKey: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: [`/api/admin/organizations/${org.id}/smtp`],
    enabled: open,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/organizations/${org.id}/smtp`);
      if (!res.ok) throw new Error("Failed to load SMTP config");
      return res.json();
    },
  });

  React.useEffect(() => {
    if (data) {
      setForm({
        enabled: data.enabled ?? false,
        provider: data.provider ?? "smtp",
        host: data.host ?? "",
        port: data.port ?? 587,
        user: data.user ?? "",
        password: "",          // never pre-fill masked password
        useSsl: data.useSsl ?? true,
        fromEmail: data.fromEmail ?? "",
        fromName: data.fromName ?? "",
        sendGridApiKey: "",    // never pre-fill masked key
      });
    }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiRequest("PUT", `/api/admin/organizations/${org.id}/smtp`, form);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Save failed");
      }
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({ title: `SMTP saved for ${org.name}` });
      setOpen(false);
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs"
        title="Configure tenant SMTP"
        onClick={() => setOpen(true)}
        data-testid={`button-smtp-${org.id}`}
      >
        <Mail className="w-3 h-3 mr-1" aria-hidden="true" />
        SMTP{org.smtpEnabled ? " ✓" : ""}
      </Button>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Email config — {org.name}</DialogTitle>
          <DialogDescription>
            Configure a custom outbound email provider for this tenant. When enabled, all emails from
            this workspace use these settings instead of the platform-wide provider.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto py-1 pr-1">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                id={`smtp-en-${org.id}`}
              />
              <Label htmlFor={`smtp-en-${org.id}`}>Use custom email provider</Label>
            </div>

            {form.enabled && (
              <>
                <div className="space-y-1.5">
                  <Label>Provider</Label>
                  <Select
                    value={form.provider}
                    onValueChange={(v) => setForm((f) => ({ ...f, provider: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smtp">SMTP</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.provider === "sendgrid" ? (
                  <div className="space-y-1.5">
                    <Label>SendGrid API Key</Label>
                    <Input
                      type="password"
                      value={form.sendGridApiKey}
                      onChange={(e) => setForm((f) => ({ ...f, sendGridApiKey: e.target.value }))}
                      placeholder={data?.sendGridApiKey ? "Leave blank to keep existing key" : "SG.xxxxx"}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Host</Label>
                      <Input
                        value={form.host}
                        onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                        placeholder="smtp.example.com"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Port</Label>
                      <Input
                        type="number"
                        value={form.port}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, port: parseInt(e.target.value) || 587 }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Username</Label>
                      <Input
                        value={form.user}
                        onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder={data?.password ? "Leave blank to keep existing" : ""}
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <Switch
                        checked={form.useSsl}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, useSsl: v }))}
                        id={`smtp-ssl-${org.id}`}
                      />
                      <Label htmlFor={`smtp-ssl-${org.id}`}>SSL / TLS</Label>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>From Email</Label>
                    <Input
                      type="email"
                      value={form.fromEmail}
                      onChange={(e) => setForm((f) => ({ ...f, fromEmail: e.target.value }))}
                      placeholder="noreply@company.com"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>From Name</Label>
                    <Input
                      value={form.fromName}
                      onChange={(e) => setForm((f) => ({ ...f, fromName: e.target.value }))}
                      placeholder="Acme Inc."
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || isLoading} data-testid={`button-save-smtp-${org.id}`}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-component: TenantUsersModal
// Shows users belonging to exactly one tenant. Uses the SA-only
// GET /api/admin/organizations/{id}/users endpoint — never leaks cross-tenant
// data. No edit/delete actions are exposed here.
// ────────────────────────────────────────────────────────────────────────────
function TenantUsersModal({
  orgId, orgName, open, onClose,
}: {
  orgId: string; orgName: string; open: boolean; onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery<any[]>({
    queryKey: [`/api/admin/organizations/${orgId}/users`],
    enabled: open && !!orgId,
    staleTime: 30_000,
  });

  const roleLabel = (role: string) => {
    switch (role) {
      case "super_admin":             return "Super Admin";
      case "admin":                   return "Admin";
      case "orgadmin": case "org_admin": return "OrgAdmin";
      case "supervisor":              return "Supervisor";
      case "liveagent": case "live_agent": return "Live Agent";
      case "auditor":                 return "Auditor";
      default:                        return "Member";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Users — {orgName}</DialogTitle>
          <DialogDescription>
            All user accounts belonging to this tenant. No API keys or sensitive credentials are shown.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="space-y-2 py-2">
              {[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : error ? (
            <div className="text-sm text-destructive py-6 text-center">
              <p className="font-medium">Failed to load users</p>
              <p className="text-xs text-muted-foreground mt-1">
                {(error as Error).message ?? "An unexpected error occurred."}
              </p>
            </div>
          ) : !data || data.length === 0 ? (
            <div className="text-center text-muted-foreground py-10 text-sm">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No users found in this tenant</p>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">Name</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">Email</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">Role</th>
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">Status</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((u: any) => (
                  <tr key={u.id} className="hover:bg-muted/20">
                    <td className="px-3 py-2.5 font-medium">{u.name || "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground text-xs font-mono">{u.email}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Badge variant={u.role === "super_admin" ? "default" : u.role === "admin" ? "secondary" : "outline"} className="text-[10px]">
                        {roleLabel(u.role)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs ${u.active === "true" || u.active === true ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.active === "true" || u.active === true ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                        {u.active === "true" || u.active === true ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="pt-2 border-t">
          <span className="text-xs text-muted-foreground mr-auto">
            {data ? `${data.length} user${data.length !== 1 ? "s" : ""}` : ""}
          </span>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

